import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { PushNotifications } from '@capacitor/push-notifications';
import { SOUNDBOARD_SOUNDS } from './constants/defaults';
import { generateMSNAvatar, TextScramble } from './utils/uiUtils';
import { retryQuery } from './utils/retryQuery';
import { useAuth } from './hooks/useAuth';
import { useRealtimeSync } from './hooks/useRealtimeSync';
import type { GameId } from './game/framework/types';
import { supabase, onAuthStateChanged, logout, loginWithGoogle, loginWithEmail, registerWithEmail, loginAnonymously } from './supabase';
import { isPlatformAdmin } from './utils/auth';
import { updateProfile } from './utils/supabase/profile';
import { motion, AnimatePresence } from 'motion/react';
import ReactMarkdown from 'react-markdown';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import CryptoJS from 'crypto-js';
import { MsnAvatar } from './components/MsnAvatar';
import { MediaPlayer } from './components/MediaPlayer';
import { LinkEmbed } from './components/LinkEmbed';
import { INVITE_JOINED_EVENT } from './components/InviteEmbed';
import { NeuralFeed } from './components/NeuralFeed';
import ErrorBoundary from './ErrorBoundary';
import VaultBrowserSidebar from './components/VaultBrowserSidebar';
import ServerRail from './components/sidebar/ServerRail';
import { useDiscordServers, useDiscordChannels } from './hooks/useDiscordServers';
import ContextPanel from './components/sidebar/ContextPanel';
import UserPanel from './components/sidebar/UserPanel';
import GameManager from './game/GameManager';
import UwUGame from './game/UwUGame';
import CallLayout from './components/CallLayout';
import { ImageModal } from './components/ImageModal';
import { SideChatPanel } from './components/SideChatPanel';
import Notification, { NotificationData } from './components/Notification';
import ModerationDialog from './components/ModerationDialog';
import { useVaultPermissions } from './services/permissionsService';
import { PermissionService, usePermission } from './services/PermissionService';
import { PERMISSION_MANAGE_SERVER, PERMISSION_EDIT_CHANNELS, PERMISSION_CREATE_CHANNELS, PERMISSION_ADMINISTRATOR } from './core/permissions/permissions';
import ProfileCustomizer from './components/ProfileCustomizer';
import ProfilePreview from './ProfilePreview';
import { UserSettingsModal } from './components/modals/UserSettingsModal';
import { VaultManagementModals } from './components/modals/VaultManagementModals';
import ServerSettingsModal from './components/modals/ServerSettingsModal';
import ChannelSettingsModal from './components/modals/ChannelSettingsModal';
import DiscordBridgeAdminModal from './components/modals/DiscordBridgeAdminModal';
import { ChatArea } from './components/chat/ChatArea';

import { ContextMenu, ContextMenuState } from './core/ui/ContextMenu';
import { FONT_FAMILIES, getNameStyle, migrateLegacyBadge, LEGACY_BADGE_MIGRATION } from './core/identity/styles';
import { useUIStore } from './stores/uiStore';

import { ServerRole, ServerMemberRole, hasPermission, getHighestRole } from './types/roles';
import { IdentityRenderer, UserBadge } from './core/identity/IdentityRenderer';
import { useEntity } from './core/resolvers/useEntity';
import GameContainer from './components/GameContainer';
import { Channel, entityResolver } from './core/resolvers/EntityResolver';
import LandingPage from './components/LandingPage';
import { AudioProcessor } from './services/audioProcessing';
import { audioRecorder } from './services/audioRecorder';
import { webRTCManager, WebRTCManager } from './services/webRTCManager';
import { VideoProcessor, DEFAULT_BACKGROUNDS } from './services/videoProcessing';
import { useCallStore } from './stores/callStore';
import { acquireCameraStream, acquireScreenStream } from './fixtures/callFixtures';
import { uploadFile } from './services/storage';
import { Search, Clock, Menu, Users, MessageSquare, Smile, Pin, Trash2, MoreVertical, Image as ImageIcon, X, Sun, Moon, Settings, Palette, User as UserIcon, Camera, Check, Phone, Video, VideoOff, Mic, MicOff, Volume2, VolumeX, Monitor, PhoneOff, Slash, Activity, Globe, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Maximize2, MapPin, Calendar, Layout, Lock as LockIcon, Hash, Plus, LogOut, Zap, ArrowLeft, ArrowRight, Square, Shield, Smartphone, CreditCard } from 'lucide-react';

import type { User } from '@supabase/auth-js';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';

import type { Emote, Message, ChatContact } from './types/chat';
import { DEFAULT_CHANNELS, CONTACTS, INITIAL_MESSAGES } from './constants/defaults';
import { sendBroadcast } from './utils/broadcast';
import { playSound, startRinging, stopRinging } from './utils/sounds';
import { NeuralButton, NeuralInput, NeuralCard, NeuralSidebarNode } from './components/ui';
import { NeuralMessageBubble } from './components/NeuralMessageBubble';
import { ChannelNameRenderer } from './components/ChannelNameRenderer';
import { useConnectionState } from './hooks/useConnectionState';
import { NeuralBackground } from './components/NeuralBackground';

// Columns vault INSERTs must request in .select() so PostgREST emits
// `RETURNING <these columns>` instead of `RETURNING *`. The vaults table has
// only column-level SELECT grants (password_hash is column-revoked, see
// 20260921010000_lock_vaults_password_hash_column.sql), so a bare `.select()`
// expands `*` to include password_hash and the INSERT dies with `42501
// permission denied` — that was the "could not create the server" bug. These
// are exactly the SELECT-granted columns, so every vault write returns a full
// UI-ready row while password_hash stays unreadable.
const VAULT_RETURN_COLUMNS =
  'id, name, description, icon_url, icon_hue, type, members, created_by, created_at, last_message_at, customization';

export default function App() {
  // --- 1. React States & Refs (Must be first) ---
  const { currentUser, isAuthenticated, isAuthChecking } = useAuth();
  const isPlatformAdminUser = usePermission(PERMISSION_ADMINISTRATOR);
  
  // Need to bring back the profile fetching effect since it relies on currentUser/isAuthenticated
  // ... this needs careful handling ...
  // Actually, for now, let's keep the profile fetch in App, but move the auth init/listener to useAuth.
  // Wait, useAuth already does the listener.
  // I need to extract the profile fetching to its own effect in App.tsx or inside useAuth.
  // Given I want to keep UI state in App, let's keep profile logic here.

  const [view, setView] = useState<'large' | 'small'>('large');
  const [baseHue, setBaseHue] = useState(() => {
    const saved = localStorage.getItem('psydechat_baseHue');
    return saved ? parseInt(saved, 10) : 175;
  });
  const [contrast, setContrast] = useState(103);
  const [saturation, setSaturation] = useState(148);
  const [vibrant, setVibrant] = useState(false);

  // LFO State
  const [lfoActive, setLfoActive] = useState(false);
  const [lfoSpeed, setLfoSpeed] = useState(1);
  const [lfoShape, setLfoShape] = useState<'sine' | 'triangle' | 'square'>('sine');
  const [showLfoControls, setShowLfoControls] = useState(false);
  const lfoRef = useRef<number>(0);
  const lfoFrameRef = useRef<number>(0);

  // Gradient State
  const [gradientMode, setGradientMode] = useState(false);
  const [showGradientControls, setShowGradientControls] = useState(false);

  const [mobileView, setMobileView] = useState<'sidebar' | 'chat'>('sidebar');
  
  const [showDMsSidebar, setShowDMsSidebar] = useState(true);
  const [showRoomsSidebar, setShowRoomsSidebar] = useState(true);
  const [activeTab, setActiveTab] = useState<'chat' | 'feed'>('chat');
  const [feedPosts, setFeedPosts] = useState<any[]>([]);
  const [feedFilter, setFeedFilter] = useState<'all' | 'following' | 'expiring'>('all');

  // UI Config State
  const [bgTheme, setBgTheme] = useState<'synthwave' | 'aurora' | 'day' | 'ocean' | 'classic' | 'aeroform' | 'monolith' | 'catppuccin' | 'tokyonight'>(() => {
    const saved = localStorage.getItem('psydechat_bgTheme');
    return (saved as any) || 'classic';
  });
  const [showUiConfig, setShowUiConfig] = useState(false);

  useEffect(() => {
    localStorage.setItem('psydechat_baseHue', baseHue.toString());
  }, [baseHue]);

  useEffect(() => {
    localStorage.setItem('psydechat_bgTheme', bgTheme);
  }, [bgTheme]);

  useEffect(() => {
    if (isAuthenticated && !isAuthChecking) {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.error(`Error attempting to enable fullscreen mode: ${err.message}`);
        });
      }
    }
  }, [isAuthenticated, isAuthChecking]);

  const [showMobileVaults, setShowMobileVaults] = useState(false);
  const [isTopBarExpanded, setIsTopBarExpanded] = useState(false);
  const [isTopBarPinned, setIsTopBarPinned] = useState(true);
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [sidebarView, setSidebarView] = useState<'dms' | 'channels'>('channels');
  const [activeSettingsCategory, setActiveSettingsCategory] = useState('Account');
  const [isPushToTalkEnabled, setIsPushToTalkEnabled] = useState(false);
  const [pushToTalkHotkey, setPushToTalkHotkey] = useState<string | null>(null);
  const [serverRoles, setServerRoles] = useState<ServerRole[]>([]);
  const [memberRoles, setMemberRoles] = useState<any[]>([]);
  const uiConfigRef = useRef<any>(null);
  const lastScrollY = useRef<number>(0);

  // Chat State
  const [showSidebarsInRoom, setShowSidebarsInRoom] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Record<string, boolean>>({});
  const [localStreamVersion, setLocalStreamVersion] = useState(0);
  const [readReceipts, setReadReceipts] = useState<Record<string, string>>({});
  const [activeGame, setActiveGame] = useState<GameId | null>(null);
  // CallLayout → GameManager wiring. The GamesRail component mounted inside
  // CallLayout's voice-control ring calls `handleLaunchGame` when the local
  // user clicks Start on a registered entry. Mirroring into App state lets:
  //   (a) the global voice-state broadcast in this file below publish
  //       `currentGame` / `currentGameSessionId` to peers (drives the 🎮
  //       pill inside each voice-channel participant's rail),
  //   (b) the legacy `<GameContainer gameId={activeGame}` mount above stay
  //       in sync so users who reached a game via the legacy nav bar land
  //       in the same activity surface as users who reached it via the rail.
  // `handleAppGameExit` is the inverse path — GameManager.onExit flips both
  // local `activeGame` and the broadcast-driven pill back to clear.
  const handleLaunchGame = useCallback((gameId: GameId) => {
    // Cast through the legacy `<GameContainer gameId={...}>` prop type
    // that accepts a plain string. New code paths use the branded GameId
    // directly so the typecheck stays strict.
    setActiveGame(gameId);
  }, []);
  const handleAppGameExit = useCallback(() => {
    setActiveGame(null);
  }, []);
  const [vaultMessages, setVaultMessages] = useState<any[]>([]);
  const [contacts, setContacts] = useState<ChatContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [vaults, setVaults] = useState<any[]>([]);
  const [dmRooms, setDmRooms] = useState<any[]>([]);
  const { servers: discordServers = [] } = useDiscordServers() || {};
  const [channels, setChannels] = useState<Channel[]>(DEFAULT_CHANNELS);
  const [chats, setChats] = useState<Record<string, Message[]>>({});
  const [typingContacts, setTypingContacts] = useState<Record<string, boolean>>({});
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [adminModeEnabled, setAdminModeEnabled] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ vaultId: string; vaultName: string } | null>(null);

  useEffect(() => {
    const cheatCode = import.meta.env.VITE_ADMIN_CHEAT_CODE?.toUpperCase();
    if (!cheatCode) return; // Cheat code disabled if env var not set

    let keyBuffer = '';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.length === 1) { // Only listen to actual character keys, not modifiers
        keyBuffer = (keyBuffer + e.key).slice(-cheatCode.length);
        if (keyBuffer.toUpperCase() === cheatCode) {
          setAdminModeEnabled(true);
          if (import.meta.env.DEV) console.log("Admin mode enabled!");
          keyBuffer = '';
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Capacitor initialization and back button hooks moved below state declarations

  // Production Reliability States
  const isOnline = useConnectionState();
  const [vaultsLoading, setVaultsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [lastMessageSentAt, setLastMessageSentAt] = useState<number>(0);
  const [messages, setMessages] = useState<Message[]>([]);

  // Chat & Call States
  const [activeContactId, setActiveContactId] = useState<string | null>(null);
  const [activeVault, setActiveVault] = useState<any>(null);

  // A Discord-linked vault's channels live in discord_channel_mappings, not in
  // the `channels` table, so the ordinary vault channel state is empty for one
  // and the sidebar falls back to showing DEFAULT_CHANNELS. Resolve the guild
  // behind the active vault — null for ordinary vaults, which short-circuits
  // the fetch — and adapt the rows into the shapes ContextPanel already renders.
  const activeDiscordGuildId = useMemo(
    () => discordServers.find((s: any) => s.vault_id === activeVault?.id)?.discord_guild_id ?? null,
    [discordServers, activeVault?.id]
  );
  const { channels: rawDiscordChannels = [] } = useDiscordChannels(activeDiscordGuildId) || {};

  // Discord models categories as channels of type 'category', while the sidebar
  // wants them as a separate list keyed by id — split rather than filter twice.
  const { discordChannels, discordCategories } = useMemo(() => {
    const cats: any[] = [];
    const chans: Channel[] = [];

    for (const row of rawDiscordChannels as any[]) {
      const entry = {
        id: row.discord_channel_id,
        name: row.discord_channel_name,
        position: row.position ?? 0,
      };

      if (row.discord_channel_type === 'category') {
        cats.push(entry);
      } else {
        chans.push({
          ...entry,
          // Discord has forum/stage/news types the sidebar has no renderer for;
          // anything that isn't voice is displayed as text rather than dropped.
          type: row.discord_channel_type === 'voice' ? 'voice' : 'text',
          category_id: row.category_id ?? null,
        });
      }
    }

    const byPosition = (a: any, b: any) => a.position - b.position;
    return { discordChannels: chans.sort(byPosition), discordCategories: cats.sort(byPosition) };
  }, [rawDiscordChannels]);

  const [vaultUnreadCounts, setVaultUnreadCounts] = useState<Record<string, number>>({});
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  const [callChannelId, setCallChannelId] = useState<string | null>(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isWatchTogetherHost, setIsWatchTogetherHost] = useState(false);
  const [callVaultId, setCallVaultId] = useState<string | null>(null);
  const [showGame, setShowGame] = useState(false);
  const [voiceParticipants, setVoiceParticipants] = useState<Record<string, any[]>>({});
  // Per-peer voice state for visual indicators (muted, camera, screenshare)
  const [peerVoiceStates, setPeerVoiceStates] = useState<Record<string, { isMuted: boolean, isCameraOn: boolean, isScreenSharing: boolean }>>({});
  const [friends, setFriends] = useState<string[]>([]);
  const [followedUsers, setFollowedUsers] = useState<string[]>([]);
  const [mutualFriends, setMutualFriends] = useState<string[]>([]);
  const [lastViewedChannels, setLastViewedChannels] = useState<Record<string, string>>({});

  // Hidden DM List State & Persistence
  const [hiddenDmUserIds, setHiddenDmUserIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('psydechat_hidden_dms');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const handleHideDM = useCallback((userId: string) => {
    setHiddenDmUserIds(prev => {
      if (prev.includes(userId)) return prev;
      const updated = [...prev, userId];
      try { localStorage.setItem('psydechat_hidden_dms', JSON.stringify(updated)); } catch {}
      return updated;
    });
    if (activeContactId === userId) {
      setActiveContactId(null);
    }
  }, [activeContactId]);

  const handleUnhideDM = useCallback((userId: string) => {
    setHiddenDmUserIds(prev => {
      if (!prev.includes(userId)) return prev;
      const updated = prev.filter(id => id !== userId);
      try { localStorage.setItem('psydechat_hidden_dms', JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  // DM Call States & Ringing Logic
  const [dmCallState, setDmCallState] = useState<'idle' | 'calling' | 'ringing'>('idle');
  const [dmCallPeer, setDmCallPeer] = useState<{ id: string; name: string; avatar: string } | null>(null);
  const ringingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const wasVoicePresenceActiveRef = useRef(false);
  const voicePresenceChannelRef = useRef<any>(null);
  const [mutualVaults, setMutualVaults] = useState<any[]>([]);

  // Lazy singleton AudioContext for local sound effects — was creating a
  // new context on every sound and ringtone tick, leaking contexts and
  // risking browser context limits. Now reuses one context.
  const soundAudioCtxRef = useRef<AudioContext | null>(null);
  const getSoundCtx = (): AudioContext => {
    if (!soundAudioCtxRef.current) {
      soundAudioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (soundAudioCtxRef.current.state === 'suspended') {
      soundAudioCtxRef.current.resume().catch(() => {});
    }
    return soundAudioCtxRef.current;
  };

  const startRinging = (isIncoming: boolean) => {
    if (ringingIntervalRef.current) return;
    const playRingTone = () => {
      try {
        const audioCtx = getSoundCtx();
        const osc1 = audioCtx.createOscillator();
        const osc2 = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        
        osc1.connect(gainNode);
        osc2.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        
        const freq1 = isIncoming ? 400 : 440;
        const freq2 = isIncoming ? 450 : 480;
        
        osc1.frequency.setValueAtTime(freq1, audioCtx.currentTime);
        osc2.frequency.setValueAtTime(freq2, audioCtx.currentTime);
        
        gainNode.gain.setValueAtTime(0.0, audioCtx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.05, audioCtx.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime + 1.2);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 1.5);
        
        osc1.start();
        osc2.start();
        osc1.stop(audioCtx.currentTime + 1.5);
        osc2.stop(audioCtx.currentTime + 1.5);
      } catch (e) {
        console.warn("Ringing audio failed", e);
      }
    };
    
    playRingTone();
    ringingIntervalRef.current = setInterval(playRingTone, 3000);
  };

  const stopRinging = () => {
    if (ringingIntervalRef.current) {
      clearInterval(ringingIntervalRef.current);
      ringingIntervalRef.current = null;
    }
  };

  const contactsRef = useRef(contacts);
  const activeTabRef = useRef(activeTab);
  const activeContactIdRef = useRef(activeContactId);

  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    activeContactIdRef.current = activeContactId;
    // Clear unread count for this contact when we open their DM
    if (activeContactId && activeTab === 'chat' && !activeVault) {
      setContacts(prev => prev.map(c => 
        c.id === activeContactId ? { ...c, unreadCount: 0 } : c
      ));
      localStorage.setItem(`last_read_dm_${activeContactId}`, new Date().toISOString());
    }
  }, [activeContactId, activeTab, activeVault]);

  useEffect(() => {
    if (activeVault && activeTab === 'chat') {
      setVaultUnreadCounts(prev => {
        if (prev[activeVault.id]) {
          return { ...prev, [activeVault.id]: 0 };
        }
        return prev;
      });
    }
  }, [activeVault, activeTab]);

  const activeVaultIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeVaultIdRef.current = activeVault?.id || null;
  }, [activeVault]);

  // Object + dmRooms mirrors for the merged message channel below. The
  // notification handler reads these so it never needs the channel to
  // re-subscribe (previously it re-subscribed on every dmRooms change —
  // churn that itself burned egress).
  const activeVaultRef = useRef<any>(null);
  useEffect(() => { activeVaultRef.current = activeVault; }, [activeVault]);
  const dmRoomsRef = useRef<any[]>([]);
  useEffect(() => { dmRoomsRef.current = dmRooms; }, [dmRooms]);

  const vaultsRef = useRef<any[]>([]);
  useEffect(() => { vaultsRef.current = vaults; }, [vaults]);

  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;

    // ONE channel handles ALL message-INSERT work. Previously there were
    // TWO channels (`global_messages_unread` + `global_messages_notifications`)
    // subscribing to the same INSERT — every message was delivered TWICE to
    // every connected client, doubling realtime egress. Both handlers now
    // live on a single subscription; the notification handler reads latest
    // UI state via refs so it never triggers a re-subscribe.
    const unreadChannel = supabase
      .channel('global_messages')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        const msg = payload.new;
        if (msg.sender_id === currentUser.id) return;

        // Membership check: only process messages for vaults the user is a member of.
        // Without this, we process every message on the platform and filter client-side,
        // which wastes bandwidth and processing for unrelated vaults.
        const isUserMember = msg.vault_id && (
          dmRoomsRef.current.some(r => r.id === msg.vault_id) ||
          vaultsRef.current.some(v => v.id === msg.vault_id)
        );
        if (!isUserMember) return;

        // DM vault ids are plain Supabase-generated UUIDs (see the `vaults`
        // inserts around sendMessage's targetOverride/activeContactId
        // branches) — they never contain a "dm-" substring, so that check
        // always evaluated false and this branch never fired, meaning the
        // sidebar unread badge never incremented for a live incoming DM.
        // A DM message is reliably distinguished by `channel_id` being
        // null (vault-channel messages always carry a channel id).
        const isDmRoom = !!msg.vault_id && !msg.channel_id;
        const isCurrentlyViewingDM = activeTabRef.current === 'chat' &&
          activeContactIdRef.current === msg.sender_id;

        if (isDmRoom && !isCurrentlyViewingDM) {
          setContacts(prev => prev.map(c =>
            c.id === msg.sender_id ? { ...c, unreadCount: (c.unreadCount || 0) + 1 } : c
          ));
        } else if (!isDmRoom && msg.vault_id) {
          if (activeVaultIdRef.current !== msg.vault_id) {
            setVaultUnreadCounts(prev => ({
              ...prev,
              [msg.vault_id]: (prev[msg.vault_id] || 0) + 1
            }));
          }
        }
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        const newMsg = payload.new;
        if (!newMsg) return;

        // Membership check: only process messages for vaults the user is a member of.
        const isUserMember = newMsg.vault_id && (
          dmRoomsRef.current.some(r => r.id === newMsg.vault_id) ||
          vaultsRef.current.some(v => v.id === newMsg.vault_id)
        );
        if (!isUserMember) return;

        // Bump the last_message_at for the room so it bubbles to the top of the DM list
        setDmRooms(prev => {
          const idx = prev.findIndex(r => r.id === newMsg.vault_id);
          if (idx !== -1) {
             const updated = [...prev];
             updated[idx] = { ...updated[idx], last_message_at: newMsg.created_at || new Date().toISOString() };
             return updated.sort((a, b) => new Date(b.last_message_at || 0).getTime() - new Date(a.last_message_at || 0).getTime());
          }
          return prev;
        });

        if (newMsg.sender_id === currentUser.id) return;
        
        // If this message is for the currently active vault, we might already see it in the chat,
        // but showing a toast or notification is still nice if the tab is hidden.
        const isActiveRoom = (activeVaultRef.current && newMsg.vault_id === activeVaultRef.current.id) || 
                             (activeContactIdRef.current && dmRoomsRef.current.some(r => r.id === newMsg.vault_id && r.members?.includes(activeContactIdRef.current)));
                             
        if (isActiveRoom && document.visibilityState === 'visible') {
          return;
        }
        
        let decryptedText = newMsg.content;
        try {
          const bytes = CryptoJS.AES.decrypt(newMsg.content, newMsg.vault_id);
          const plaintext = bytes.toString(CryptoJS.enc.Utf8);
          if (plaintext) decryptedText = plaintext;
        } catch (e) {}
        
        const title = `Message from ${newMsg.sender_name || 'Friend'}`;
        addNotification({
          type: 'system',
          title,
          message: decryptedText.length > 60 ? decryptedText.slice(0, 57) + '...' : decryptedText
        });
        
        playSound('message');
        
        if ('Notification' in window && window.Notification.permission === 'granted') {
          new window.Notification(title, {
            body: decryptedText,
            icon: newMsg.sender_avatar || generateMSNAvatar(newMsg.sender_id)
          });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(unreadChannel);
    };
  }, [isAuthenticated, currentUser?.id]);

  const acceptDmCall = () => {
    if (!dmCallPeer) return;
    stopRinging();
    const peerId = dmCallPeer.id;
    const channelId = [currentUser!.id, peerId].sort().join("-");
    sendBroadcast(`user-${peerId}`, 'dm-call-accept', { senderId: currentUser?.id, channelId });
    setDmCallState('idle');
    setDmCallPeer(null);
    
    setCallChannelId(channelId);
    setIsCallActive(true);
    setCallVaultId(null);
    setCallDuration(0);
    startCallTimer();
    playSound('join');
  };

  const declineDmCall = () => {
    if (!dmCallPeer) return;
    stopRinging();
    sendBroadcast(`user-${dmCallPeer.id}`, 'dm-call-decline', { senderId: currentUser?.id });
    setDmCallState('idle');
    setDmCallPeer(null);
  };

  const cancelDmCall = () => {
    if (!dmCallPeer) return;
    stopRinging();
    sendBroadcast(`user-${dmCallPeer.id}`, 'dm-call-cancel', { senderId: currentUser?.id });
    setDmCallState('idle');
    setDmCallPeer(null);
  };

  const toggleFollow = async (userId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const uid = user.id;
    const isFollowing = followedUsers.includes(userId);
    try {
      if (isFollowing) {
        await supabase.from('follows').delete().match({ follower_id: uid, following_id: userId });
        setFollowedUsers(prev => prev.filter(id => id !== userId));
      } else {
        await supabase.from('follows').insert({ follower_id: uid, following_id: userId });
        setFollowedUsers(prev => [...prev, userId]);
      }
    } catch (error) { console.error(error); }
  };

  const handleFriendRequest = async (userId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const uid = user.id;
    try {
      await supabase.from('friends').upsert({ user_id: uid, friend_id: userId, status: 'accepted' });
      await supabase.from('friends').upsert({ user_id: userId, friend_id: uid, status: 'accepted' });
      setFriends(prev => [...prev, userId]);
      setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Friend Added', message: 'You are now friends!', time: 'Now' }]);
    } catch (error) { console.error(error); }
  };

  const playSound = (type: 'join' | 'leave' | 'mute' | 'unmute' | 'toggle' | 'message') => {
    try {
      const audioCtx = getSoundCtx();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      let duration = 0.2;
      oscillator.type = 'sine';

      switch (type) {
        case 'join':
          oscillator.frequency.setValueAtTime(440, audioCtx.currentTime);
          oscillator.frequency.exponentialRampToValueAtTime(880, audioCtx.currentTime + 0.1);
          gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime);
          break;
        case 'leave':
          oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
          oscillator.frequency.exponentialRampToValueAtTime(440, audioCtx.currentTime + 0.1);
          gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime);
          break;
        case 'mute':
          oscillator.frequency.setValueAtTime(330, audioCtx.currentTime);
          oscillator.frequency.linearRampToValueAtTime(220, audioCtx.currentTime + 0.05);
          gainNode.gain.setValueAtTime(0.03, audioCtx.currentTime);
          duration = 0.1;
          break;
        case 'unmute':
          oscillator.frequency.setValueAtTime(220, audioCtx.currentTime);
          oscillator.frequency.linearRampToValueAtTime(330, audioCtx.currentTime + 0.05);
          gainNode.gain.setValueAtTime(0.03, audioCtx.currentTime);
          duration = 0.1;
          break;
        case 'toggle':
          oscillator.frequency.setValueAtTime(660, audioCtx.currentTime);
          gainNode.gain.setValueAtTime(0.02, audioCtx.currentTime);
          duration = 0.05;
          break;
        case 'message':
          oscillator.frequency.setValueAtTime(600, audioCtx.currentTime);
          oscillator.frequency.exponentialRampToValueAtTime(1200, audioCtx.currentTime + 0.08);
          gainNode.gain.setValueAtTime(0.04, audioCtx.currentTime);
          duration = 0.15;
          break;
      }

      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + duration);
    } catch (e) {
      console.warn("AudioContext failed", e);
    }
  };

  // Expanded Call States
  const [isPipMode, setIsPipMode] = useState(false);
  const [pipPosition, setPipPosition] = useState({ x: 24, y: 80 }); 
  const [isDraggingPip, setIsDraggingPip] = useState(false);
  const pipDragOffset = useRef({ x: 0, y: 0 });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCinemaMode, setIsCinemaMode] = useState(false);
  const [isVaultBrowserCollapsed, setIsVaultBrowserCollapsed] = useState(false);
  const [activeSpeakerId, setActiveSpeakerId] = useState<string>('glitch');
  const [isMediaPlayerPoppedOut, setIsMediaPlayerPoppedOut] = useState(false);
  // 'speaker' is included so this state setter is assignable to CallLayout's
  // setLayoutMode prop, which accepts 'speaker' | 'focus' | 'tile'.
  const [layoutMode, setLayoutMode] = useState<'focus' | 'tile' | 'speaker'>('focus');
  const [showSideChat, setShowSideChat] = useState(false);
  const [focusedParticipantId, setFocusedParticipantId] = useState<string | null>(null);
  const [hiddenParticipants, setHiddenParticipants] = useState<string[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const [sbVolume, setSbVolume] = useState(0.05);
  const [callReactions, setCallReactions] = useState<any[]>([]);

  const handleSendCallReaction = (emoji: string) => {
    const reaction = {
      id: Date.now().toString(),
      userId: currentUser?.id || 'me',
      emoji,
      createdAt: Date.now()
    };
    setCallReactions(prev => [...prev, reaction]);
    // Socket emit would go here for multi-user sync
    setTimeout(() => {
      setCallReactions(prev => prev.filter(r => r.id !== reaction.id));
    }, 4000);
  };

  const sbVolumeRef = useRef(0.05);
  useEffect(() => { sbVolumeRef.current = sbVolume; }, [sbVolume]);

  const handlePlaySoundboard = (soundUrl: string) => {
    const audio = new Audio(soundUrl);
    audio.volume = sbVolumeRef.current;
    audio.play().catch(err => console.error("Soundboard play failed", err));
    // Socket emit would go here for multi-user sync
  };
  const [isDeafened, setIsDeafened] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [isAudioProcessingEnabled, setIsAudioProcessingEnabled] = useState(true);
  const [isLimiterEnabled, setIsLimiterEnabled] = useState(true);

  const [isNormalizerEnabled, setIsNormalizerEnabled] = useState(true);
  const [limiterThreshold, setLimiterThreshold] = useState(-1);
  const [normalizerThreshold, setNormalizerThreshold] = useState(-24);
  const [outputGain, setOutputGain] = useState(1.0);
  const [isNoiseSuppressionEnabled, setIsNoiseSuppressionEnabled] = useState(true);
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const [showScreenShareOptions, setShowScreenShareOptions] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [customEmotes, setCustomEmotes] = useState<Emote[]>([]);
  const [emoteSearch, setEmoteSearch] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  
  const [mutedUsers, setMutedUsers] = useState<string[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
  // Privacy > Friend Requests selector — local component state only.
  // Backend wire-up pending: this drives only the UI radio group, it
  // does not gate the existing `handleFriendRequest` flow on the server.
  const [allowFriendFrom, setAllowFriendFrom] = useState<'everyone' | 'shared_servers' | 'mutual_friends' | 'nobody'>('everyone');

  // User Profile State
  const [userProfile, setUserProfile] = useState({
    name: 'User',
    avatar: generateMSNAvatar('User'),
    status: 'Online',
    bio: '',
    presence: 'Online',
    bannerType: 'color', 
    bannerValue: '#1e1e1e',
    gradientHue1: 260,
    gradientSat1: 80,
    gradientHue2: 320,
    gradientSat2: 80,
    bannerImage: '',
    profileBackgroundType: 'color', 
    profileBackgroundValue: '#040711',
    profileBackgroundGradient: { hue1: 280, sat1: 90, hue2: 300, sat2: 90 },
    profileBackgroundImage: '',
    profileTextStyle: { fontSize: '14px', color: '#ffffff', fontFamily: 'Inter' },
    profileBorderStyle: { color: '#ffffff', gradient: 'none' },
    integrations: { instagram: '', soundcloud: '', github: '' },
    games: '',
    inCall: false,
    vaultName: '',
    customization: {
      font: 'Inter',
      nameStyle: 'Neon',
      palette: 'Cyberpunk',
      color1: '#818cf8',
      color2: '#22d3ee',
      texture: 'None',
      avatarDecoration: 'None',
      auraMode: 'None',
      auraColor1: '#22d3ee',
      auraColor2: '#c084fc',
      statusMessage: '',
    }
  });

  // Ref-mirror of userProfile so async timers (e.g. the V3 video-effect
  // save below) can read the LATEST customization at fire time rather
  // than a stale closure snapshot. Without this, the 600ms debounce
  // window could overwrite a ProfileCustomizer save that landed in
  // between Effect 4 firing and the timer running.
  const userProfileRef = useRef(userProfile);
  useEffect(() => { userProfileRef.current = userProfile; }, [userProfile]);

  const avatarUploadInputRef = useRef<HTMLInputElement>(null);

  const handleAppAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    try {
      setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Uploading Avatar', message: 'Your new identity is being uploaded...', time: 'Now' }]);
      const filePath = `${currentUser.id}/avatars/${Date.now()}_${file.name}`;
      const imageUrl = await uploadFile(file, filePath);

      await supabase.from('profiles').update({ photo_url: imageUrl }).eq('id', currentUser.id);
      setUserProfile(prev => ({ ...prev, avatar: imageUrl }));
      setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Avatar Updated', message: 'Your avatar has been updated!', time: 'Now' }]);
    } catch (error) {
      console.error('Avatar upload failed:', error);
      setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Upload Failed', message: 'Could not upload avatar.', time: 'Now' }]);
    }
  };

  const handleVaultIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Uploading Icon', message: 'Processing vault identity...', time: 'Now' }]);
      const imageUrl = await uploadFile(file);
      setNewRoomIcon(imageUrl);
      setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Icon Updated', message: 'Vault icon uploaded successfully!', time: 'Now' }]);
    } catch (error) {
      console.error('Vault icon upload failed:', error);
      setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Upload Failed', message: 'Could not upload vault icon.', time: 'Now' }]);
    }
  };


  const [draftProfile, setDraftProfile] = useState(userProfile);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const profileSettingsRef = useRef<HTMLDivElement>(null);

  const [showCreateVaultModal, setShowCreateVaultModal] = useState(false);
  const [isEditingVault, setIsEditingVault] = useState(false);

  const [showCreateChannelModal, setShowCreateChannelModal] = useState(false);
  const [showServerSettingsModal, setShowServerSettingsModal] = useState(false);
  const [serverSettingsInitialTab, setServerSettingsInitialTab] = useState<any>('Overview');
  const [serverSettingsInitialEditId, setServerSettingsInitialEditId] = useState<string | null>(null);
  const [showDiscordBridgeAdmin, setShowDiscordBridgeAdmin] = useState(false);
  const [showChannelSettingsModal, setShowChannelSettingsModal] = useState(false);
  const [editingSettingsChannelId, setEditingSettingsChannelId] = useState<string | null>(null);
  const [settingsVaultId, setSettingsVaultId] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [newChannelType, setNewChannelType] = useState<'text' | 'voice'>('text');
  const [editingChannelId, setEditingChannelId] = useState<string | null>(null);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [vaultMembers, setVaultMembers] = useState<any[]>([]);
  const [showClearNodesConfirm, setShowClearNodesConfirm] = useState(false);
  const [showVaultDropdown, setShowVaultDropdown] = useState(false);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);
  const [messageRecipientId, setMessageRecipientId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState("");
  const [newVaultName, setNewRoomName] = useState("");
  const [newVaultDesc, setNewRoomDesc] = useState("");
  const [newVaultIsPublic, setNewRoomIsPublic] = useState(true);
  const [newVaultHasPassword, setNewRoomHasPassword] = useState(false);
  const [newVaultPassword, setNewRoomPassword] = useState("");
  const [newVaultIcon, setNewRoomIcon] = useState("");
  const [newVaultInvitedUsers, setNewRoomInvitedUsers] = useState<string[]>([]);
  const [newChannelName, setNewChannelName] = useState("");
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordToJoin, setPasswordToJoin] = useState("");
  const [vaultToJoin, setVaultToJoin] = useState<any>(null);

  const [inputValue, setInputValue] = useState("");
  const [attachments, setAttachments] = useState<{ id: string; url?: string; type: 'image' | 'video' | 'audio'; uploading: boolean; file?: File }[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pickerTab, setPickerTab] = useState<'emoji' | 'gif' | 'emotes'>('emoji');
  const [pickerMode, setPickerMode] = useState<'message' | 'reaction'>('message');
  const [pickerTargetId, setPickerTargetId] = useState<string | null>(null);
  const [showPins, setShowPins] = useState(false);
  const [expiryTime, setExpiryTime] = useState<number>(8 * 60 * 60 * 1000); 
  const [showTimerSettings, setShowTimerSettings] = useState(false);
  const [isShiftHeld, setIsShiftHeld] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const viewingProfileId = useUIStore(s => s.viewingProfileId);
  const setViewingProfileId = useUIStore(s => s.setViewingProfileId);

  
  // Device Selection State
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('default');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('default');

  // Global Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowUserSettings(false);
        setShowUiConfig(false);
        setViewingProfileId(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [setViewingProfileId]);

  // Capacitor Native Plugins Initialization
  useEffect(() => {
    // Hardware Back Button
    const backButtonListener = CapacitorApp.addListener('backButton', () => {
      if (showUserSettings) {
        setShowUserSettings(false);
      } else if (showUiConfig) {
        setShowUiConfig(false);
      } else if (viewingProfileId) {
        setViewingProfileId(null);
      } else {
        // If at root, you could minimize the app
        CapacitorApp.minimizeApp();
      }
    });

    // Push Notifications
    if (Capacitor.isNativePlatform()) {
      PushNotifications.requestPermissions().then(result => {
        if (result.receive === 'granted') {
          PushNotifications.register();
        }
      });
      PushNotifications.addListener('registration', (token) => {
        if (import.meta.env.DEV) console.log('Push registration success, token: ' + token.value);
      });
    }

    return () => {
      backButtonListener.then(listener => listener.remove());
    };
  }, [showUserSettings, showUiConfig, viewingProfileId, setViewingProfileId]);

  // Keep screen awake during active calls
  useEffect(() => {
    if (isCallActive) {
      KeepAwake.keepAwake().catch(console.error);
    } else {
      KeepAwake.allowSleep().catch(console.error);
    }
  }, [isCallActive]);
  const handleToggleRecording = async () => {
    if (isRecording) {
      const blob = await audioRecorder.stop();
      setIsRecording(false);
      
      // CSPRNG-derived upload placeholder (was Math.random — predictable).
      const uploadId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID().slice(0, 12)
          : Math.random().toString(36).substring(7);
      const placeholder = `Uploading audio...`;
      insertHtmlAtCursor(`<span class="upload-status" data-upload-id="${uploadId}">${placeholder}</span>`);
      
      try {
        const url = await uploadFile(blob, 'voice_message.webm');
        const markdown = `![audio](${url})`;
        const html = `<img src="${url}" alt="audio" data-markdown="${markdown}" /> `;
        
        if (richInputRef.current) {
          const statusEl = richInputRef.current.querySelector(`.upload-status[data-upload-id="${uploadId}"]`);
          if (statusEl) {
            statusEl.outerHTML = html;
          } else {
            insertHtmlAtCursor(html);
          }
        }
      } catch (err) {
        console.error("Audio upload failed", err);
        if (richInputRef.current) {
          const statusEl = richInputRef.current.querySelector(`.upload-status[data-upload-id="${uploadId}"]`);
          if (statusEl) statusEl.textContent = '[Audio Upload Failed]';
        }
      }
    } else {
      try {
        await audioRecorder.start();
        setIsRecording(true);
      } catch (err) {
        console.error("Failed to start recording", err);
      }
    }
  };

  const handleFileUpload = async (file: File) => {
    if (import.meta.env.DEV) console.log('handleFileUpload triggered for:', file.name, 'type:', file.type);
    const typeLabel = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'audio';
    const uploadPlaceholder = `*[Uploading ${typeLabel}...]*`;

    setInputValue(prev => {
      const newVal = prev + (prev.length > 0 && !prev.endsWith('\n') ? '\n' : '') + uploadPlaceholder;
      if (richInputRef.current) richInputRef.current.innerText = newVal;
      return newVal;
    });

    try {
      if (import.meta.env.DEV) console.log('Calling uploadFile service...');
      const url = await uploadFile(file);
      if (import.meta.env.DEV) console.log('uploadFile service returned URL:', url);
      const markdown = file.type.startsWith('image/') ? `![image](${url})` : 
                       file.type.startsWith('video/') ? `![video](${url})` : 
                       `![audio](${url})`;
      
      setInputValue(prev => {
        const newVal = prev.replace(uploadPlaceholder, markdown);
        if (richInputRef.current) richInputRef.current.innerText = newVal;
        return newVal;
      });
    } catch (err) {
      console.error(`${typeLabel} upload failed`, err);
      setInputValue(prev => {
        const newVal = prev.replace(uploadPlaceholder, `*[${typeLabel} Upload Failed]*`);
        if (richInputRef.current) richInputRef.current.innerText = newVal;
        return newVal;
      });
    }
  };
  
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    x: 0,
    y: 0,
    type: null,
    targetId: '',
    visible: false
  });

  // Moderation dialog — anchored at the right-click coordinate, surfaced
  // for any right-click on a user when the actor holds at least one
  // moderation permission in the active vault.
  const [moderationDialog, setModerationDialog] = useState<
    { anchor: { x: number; y: number }; targetId: string; targetName?: string } | null
  >(null);

  // Permission snapshot for the active vault — re-fetched every 60s by
  // the service, so this is functional for whoever is in scan-distance
  // of a stale value. Used by the moderation dialog + the UserPanel
  // "Manage Roles" button visibility, etc.
  const vaultPerms = useVaultPermissions(activeVault?.id);

  const [settingNicknameForUser, setSettingNicknameForUser] = useState<string | null>(null);
  const [nicknames, setNicknames] = useState<Record<string, string>>({});
  const [nicknameInput, setNicknameInput] = useState("");

  const callTimerRef = useRef<NodeJS.Timeout | null>(null);
  // (Re)start the call-duration ticker. Always clears any existing interval
  // first so the 5 call-entry paths below (DM accept via 2 broadcast
  // events, the callChannelId effect, and 2 direct join paths) can never
  // stack a second interval on top of one already running.
  const startCallTimer = useCallback(() => {
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    callTimerRef.current = setInterval(() => { setCallDuration(prev => prev + 1); }, 1000);
  }, []);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const screenVideoRef = useRef<HTMLVideoElement>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const processedStreamRef = useRef<MediaStream | null>(null);
  const audioProcessorRef = useRef<AudioProcessor | null>(null);
  // V2 video segmentation/blur pipeline — mirrors AudioProcessor's
  // lifecycle exactly but for the video plane. Instantiated and torn
  // down by the V2 lifecycle useEffect below based on (videoEffectMode,
  // isVideoOn, localStreamVersion) so the canvas+pipeline lives only
  // while a non-`none` background mode is active AND the camera is on.
  const videoProcessorRef = useRef<VideoProcessor | null>(null);
  // Defense-in-depth unmount cleanup: the normal teardown goes through
  // endCall() → stop() → parentNode.removeChild() (the video-leak fix),
  // but if the App component unmounts via a non-endCall path (route
  // change, hot-reload, error-boundary swallow) React tears down without
  // running our explicit cleanup and the hidden <video> outlives the
  // React tree. One-line `() => proc?.stop()` covers the gap.
  useEffect(() => () => {
    const proc = videoProcessorRef.current;
    if (proc) {
      try { proc.stop(); } catch { /* ignore */ }
      videoProcessorRef.current = null;
    }
  }, []);
  // ---- V2 video-effect persistence refs (Round-6 follow-up) ----
  // hydratedRef flips to true AFTER a profile fetch resolves (with or
  // without persisted fx). Flips to false on user-switch so a new user
  // doesn't inherit a previous session's lastSaved snapshot. The save
  // effect below early-returns while this is false — that's Defense A
  // against the hydration-vs-save race the thinker flagged: a freshly
  // mounted defaults-only callStore must never overwrite a real DB row.
  const videoEffectHydratedRef = useRef(false);
  // lastSavedVideoFxRef mirrors the snapshot we just read from (or
  // wrote to) profile.customization.videoEffect*. The save effect's
  // diff guard skips no-op writes when the callStore matches this.
  const lastSavedVideoFxRef = useRef<{ mode: string; bg: string | null; intensity: number } | null>(null);
  // Holds the debounced writeback timer so a slider-drag burst only
  // writes one row. Cleared on every dep change and on unmount.
  const videoFxSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Reset the hydration flag + diff guard on user switch so the next
  // user's fetchProfile applies a clean snapshot rather than mixing
  // the previous session's writes into fresh-user writes.
  useEffect(() => {
    videoEffectHydratedRef.current = false;
    lastSavedVideoFxRef.current = null;
    if (videoFxSaveTimerRef.current) {
      clearTimeout(videoFxSaveTimerRef.current);
      videoFxSaveTimerRef.current = null;
    }
  }, [currentUser?.id]);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const sloganRef = useRef<HTMLSpanElement>(null);
  const chatScrollerRef = useRef<HTMLDivElement>(null);
  const richInputRef = useRef<HTMLDivElement>(null);
  const webRTCManagerRef = useRef<WebRTCManager | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const vaultIconInputRef = useRef<HTMLInputElement>(null);
  const lfoControlsRef = useRef<HTMLDivElement>(null);
  const gradientControlsRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTypingTimeRef = useRef<number>(0);

  // ---------- Mobile swipe-back gesture ----------
  // Only active on mobile (≤768px, handled in CSS) and only when mobileView
  // is anything other than 'lobby'. Tracks a touch that BEGINS in the left
  // edge zone; ignores vertical-scroll intent; navigates back to lobby on
  // a successful rightward swipe past the threshold.
  const swipeRef = useRef<{ id: number; x: number; y: number; aborted: boolean } | null>(null);
  const [swipeProgress, setSwipeProgress] = useState(0);

  // ---------- Mobile-call overlay + chat peek ----------
  // Rendered when isCallActive && viewport ≤ 768px. The call layout (both
  // in-place and PIP branches) is wrapped in .mobile-call-full which goes
  // full-screen via CSS; the chat collapses into a peek at the bottom.
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(max-width: 768px)');
    const handleChange = () => setIsMobileViewport(mql.matches);
    mql.addEventListener('change', handleChange);
    handleChange();
    return () => mql.removeEventListener('change', handleChange);
  }, []);
  const [chatPeekExpanded, setChatPeekExpanded] = useState(false);
  // Collapse the peek automatically whenever the call ends or the user
  // navigates between vault contexts.
  useEffect(() => {
    if (!isCallActive) setChatPeekExpanded(false);
  }, [isCallActive]);

  const handleChatTouchStart = (e: React.TouchEvent<HTMLElement>) => {
    if (mobileView === 'sidebar') return;
    const t = e.changedTouches[0];
    // Only initiate the gesture if the touch began in the left edge zone.
    if (t.clientX > 32) {
      swipeRef.current = null;
      return;
    }
    swipeRef.current = { id: t.identifier, x: t.clientX, y: t.clientY, aborted: false };
    setSwipeProgress(0);
  };

  const handleChatTouchMove = (e: React.TouchEvent<HTMLElement>) => {
    const sw = swipeRef.current;
    if (!sw || sw.aborted) return;
    const t = Array.from(e.changedTouches).find((c) => c.identifier === sw.id);
    if (!t) return;

    const dx = t.clientX - sw.x;
    const dy = Math.abs(t.clientY - sw.y);
    // If the user is clearly scrolling vertically, abandon the gesture.
    if (dy > 50 && dy > Math.abs(dx)) {
      sw.aborted = true;
      setSwipeProgress(0);
      return;
    }
    if (dx > 0) setSwipeProgress(Math.min(1, dx / 120));
  };

  const handleChatTouchEnd = (e: React.TouchEvent<HTMLElement>) => {
    const sw = swipeRef.current;
    if (!sw || sw.aborted) {
      swipeRef.current = null;
      setSwipeProgress(0);
      return;
    }
    const t = Array.from(e.changedTouches).find((c) => c.identifier === sw.id);
    swipeRef.current = null;
    if (!t) {
      setSwipeProgress(0);
      return;
    }
    const dx = t.clientX - sw.x;
    if (dx > 80) {
      // Successful swipe-back → return to lobby, mirroring the back button.
      setActiveTab('chat');
      setMobileView('sidebar');
    }
    setSwipeProgress(0);
  };

  useEffect(() => {
    const fetchFeed = async () => {
      const { data, error } = await supabase
        .from('feed_posts')
        .select(`
          id,
          author_id,
          author_name,
          author_avatar,
          content,
          created_at,
          expires_at,
          likes,
          reply_count,
          feed_replies (
            id,
            author_id,
            author_name,
            author_avatar,
            content,
            created_at
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50);
      if (data) {
        const mappedPosts = data.map(post => ({
          id: post.id,
          authorId: post.author_id,
          authorName: post.author_name,
          authorAvatar: post.author_avatar,
          content: post.content,
          createdAt: post.created_at,
          expiresAt: post.expires_at ? new Date(post.expires_at).getTime() : null,
          likes: post.likes || {},
          replyCount: post.reply_count || 0,
          replies: (post.feed_replies || []).sort((a: any, b: any) => 
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          )
        }));
        setFeedPosts(mappedPosts);
      }
      if (error) console.error(error);
    };

    fetchFeed();

    const channel = supabase
      .channel('feed_posts_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'feed_posts' }, (payload) => {
        // Local patch instead of full `fetchFeed()` refetch: the previous
        // `fetchFeed()` on every event re-pulled the ENTIRE feed + all
        // nested replies for EVERY connected client (uncached egress burn).
        // The realtime payload carries the changed row, so we can patch the
        // list in place — zero extra queries per event.
        if (payload.eventType === 'INSERT' && payload.new) {
          const p = payload.new;
          const newPost = {
            id: p.id,
            authorId: p.author_id,
            authorName: p.author_name,
            authorAvatar: p.author_avatar,
            content: p.content,
            createdAt: p.created_at,
            expiresAt: p.expires_at ? new Date(p.expires_at).getTime() : null,
            likes: p.likes || {},
            replyCount: p.reply_count || 0,
            replies: [],
          };
          setFeedPosts(prev => [newPost, ...prev.filter(x => x.id !== newPost.id)]);
        } else if (payload.eventType === 'UPDATE' && payload.new) {
          const p = payload.new;
          setFeedPosts(prev => prev.map(post =>
            post.id === p.id ? {
              ...post,
              authorName: p.author_name ?? post.authorName,
              authorAvatar: p.author_avatar ?? post.authorAvatar,
              content: p.content ?? post.content,
              createdAt: p.created_at ?? post.createdAt,
              expiresAt: p.expires_at ? new Date(p.expires_at).getTime() : null,
              likes: p.likes || post.likes || {},
              replyCount: p.reply_count ?? post.replyCount,
            } : post
          ));
        } else if (payload.eventType === 'DELETE' && payload.old) {
          setFeedPosts(prev => prev.filter(post => post.id !== payload.old.id));
        }
      })
      .subscribe();

    // feed_replies fires its OWN realtime events (it's in the realtime
    // publication) and feed_posts is NOT touched when a reply lands — so
    // patching feed_posts alone would leave the `replies` array stale.
    // Subscribe to reply INSERT/DELETE and patch the parent post's reply
    // list locally (zero queries). Keeps reply shape identical to what
    // fetchFeed produced (raw snake_case DB fields).
    const repliesChannel = supabase
      .channel('feed_replies_changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'feed_replies' }, (payload) => {
        const r = payload.new;
        if (!r || !r.post_id) return;
        const reply = {
          id: r.id,
          post_id: r.post_id,
          author_id: r.author_id,
          author_name: r.author_name,
          author_avatar: r.author_avatar,
          content: r.content,
          created_at: r.created_at,
        };
        setFeedPosts(prev => prev.map(post =>
          post.id === r.post_id
            ? {
                ...post,
                replyCount: (post.replyCount || 0) + 1,
                replies: [...(post.replies || []), reply].sort((a: any, b: any) =>
                  new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                ),
              }
            : post
        ));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'feed_replies' }, (payload) => {
        const old = payload.old;
        if (!old || !old.post_id) return;
        setFeedPosts(prev => prev.map(post =>
          post.id === old.post_id
            ? {
                ...post,
                replyCount: Math.max(0, (post.replyCount || 0) - 1),
                replies: (post.replies || []).filter(r => r.id !== old.id),
              }
            : post
        ));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(repliesChannel);
    };
  }, []);

  // --- 2. Computed Logic (Memos) ---
  const activeContact = useMemo(() => contacts.find(c => c.id === activeContactId), [contacts, activeContactId]);
  const displayedMessages = useMemo(() => (!activeVault && activeContactId && blockedUsers.includes(activeContactId)) ? [] : messages, [activeVault, activeContactId, blockedUsers, messages]);

  // Mobile-call overlay + chat-peek helpers. Declared here (after the memos
  // above) because they read `activeContact` and `displayedMessages`; placing
  // them earlier in the function body — as was done before — hits a Temporal
  // Dead Zone on first render and produces the production-side
  // `Cannot access 'Ct' before initialization` crash.
  const mobileCallOverlayActive = isCallActive && isMobileViewport && !isPipMode;
  const peekTitle = activeVault?.name
    || activeContact?.name
    || (callVaultId ? 'Voice Channel' : 'DM Call');
  const peekRecentMessages = displayedMessages.slice(-3);

  // Shared list of participants for both the in-place call view (line ~3808)
  // and the mobile-only full-screen call overlay. Computed from the call's
  // voice-presence roster (server calls) or the DM contact (DM calls).
  const currentCallParticipants = useMemo(() => {
    const me = { id: currentUser?.id || 'me', name: userProfile.name, avatar: userProfile.avatar, isWatchTogetherHost };
    if (callVaultId) {
      return voiceParticipants[callChannelId || 'vc'] || [me];
    }
    if (callChannelId) {
      const friend = contacts.find(c => c.id === callChannelId.replace('dm-', ''));
      return friend ? [me, { id: friend.id, name: friend.name, avatar: friend.avatar }] : [me];
    }
    return [me];
  }, [callChannelId, callVaultId, voiceParticipants, contacts, currentUser?.id, userProfile.name, userProfile.avatar, isWatchTogetherHost]);

  const contact = useMemo(() => {
    return (viewingProfileId === 'me' 
      ? { 
          id: 'me', 
          name: userProfile.name, 
          avatar: userProfile.avatar, 
          status: userProfile.status, 
          bio: 'This is you!', 
          joinDate: 'Today', 
          location: 'Localhost', 
          type: 'human',
          bannerType: userProfile.bannerType as 'color' | 'gradient' | 'image',
          bannerValue: userProfile.bannerValue,
          gradientHue1: userProfile.gradientHue1,
          gradientSat1: userProfile.gradientSat1,
          gradientHue2: userProfile.gradientHue2,
          gradientSat2: userProfile.gradientSat2,
          bannerImage: userProfile.bannerImage,
          profileBackgroundType: userProfile.profileBackgroundType as 'color' | 'gradient' | 'image',
          profileBackgroundValue: userProfile.profileBackgroundValue,
          profileBackgroundGradient: userProfile.profileBackgroundGradient,
          profileBackgroundImage: userProfile.profileBackgroundImage,
          profileTextStyle: userProfile.profileTextStyle,
          profileBorderStyle: userProfile.profileBorderStyle
        }
      : contacts.find(c => c.id === viewingProfileId)) as ChatContact | undefined;
  }, [viewingProfileId, userProfile, contacts]);

  const [remoteStreams, setRemoteStreams] = useState<Record<string, MediaStream>>({});
  const [remoteScreenStreams, setRemoteScreenStreams] = useState<Record<string, MediaStream>>({});
  
  // --- WebRTC Event Handlers Refs ---
  // We use refs to avoid closure problems in webRTCManager handlers
  const remoteStreamsRef = useRef<Record<string, MediaStream>>({});
  const isCallActiveRef = useRef(false);

  useEffect(() => {
    isCallActiveRef.current = isCallActive;
  }, [isCallActive]);

  useEffect(() => {
    remoteStreamsRef.current = remoteStreams;
  }, [remoteStreams]);

  // Track pending calls to avoid double-initiating
  const pendingCallsRef = useRef<Set<string>>(new Set());
  const callVaultIdRef = useRef<string | null>(null);
  const endCallRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    callVaultIdRef.current = callVaultId;
  }, [callVaultId]);

  // --- 3. Effects (Must follow states) ---
  useEffect(() => {
    if (!currentUser?.id) return; // Ensure currentUser is available before initializing

    const initializeWebRTC = async () => {
      webRTCManagerRef.current = webRTCManager;
      await webRTCManagerRef.current.init(currentUser.id);

      webRTCManagerRef.current.setEventHandlers({
        onRemoteStream: (stream, peerId, isScreen) => {
          if (import.meta.env.DEV) console.log(`[WebRTC] Received stream from ${peerId}`, stream.getTracks().map(t => t.kind));
          if (isScreen) {
             setRemoteScreenStreams(prev => ({ ...prev, [peerId]: stream }));
          } else {
             setRemoteStreams(prev => ({ ...prev, [peerId]: stream }));
          }
          pendingCallsRef.current.delete(peerId);
        },
        onLocalStream: (stream) => {
          localStreamRef.current = stream;
          setLocalStreamVersion(v => v + 1);
        },
        onCallEnded: (peerId, isScreen) => {
          if (isScreen) {
             setRemoteScreenStreams(prev => {
                const newStreams = { ...prev };
                delete newStreams[peerId];
                return newStreams;
             });
          } else {
             setRemoteStreams(prev => {
                const newStreams = { ...prev };
                delete newStreams[peerId];
                return newStreams;
             });
             setPeerVoiceStates(prev => {
                const newStates = { ...prev };
                delete newStates[peerId];
                return newStates;
             });
             pendingCallsRef.current.delete(peerId);
             if (!callVaultIdRef.current && Object.keys(remoteStreamsRef.current).length <= 1) {
               endCallRef.current?.();
             }
          }
        },
        onError: (error) => {
          console.error(`[WebRTC Error]: ${error}`);
          addNotification({ type: 'system', title: 'WebRTC Error', message: error });
        },
        onLocalStreamUnavailable: (reason) => {
          // Soft UX for benign denials (permission blocked, no devices,
          // OS-level hardware conflict). The user still joins the room
          // as a passive receiver — we just explain why their own tile
          // is silent/black instead of throwing a scary "Permission
          // denied" error toast.
          const messages: Record<string, string> = {
            NotAllowedError: 'Camera/mic access was blocked. You can still hear others — click the camera/mic to retry.',
            NotFoundError: 'No camera or microphone detected. You can still hear others.',
            NotReadableError: 'Camera or mic is in use by another app.',
            SecurityError: 'Browser blocked media access (check HTTPS). You can still hear others.',
          };
          const message = messages[reason];
          // AbortError = cancelled mid-prompt, no toast needed
          if (!message) return;
          console.info(`[WebRTC] Local media unavailable (${reason}); joining in receive-only mode.`);
          addNotification({ type: 'system', title: 'Listening only', message });
        },
        onIncomingCall: async (offer, remotePeerId) => {},
        onSpeakingChanged: (peerId, isSpeaking) => {
          setActiveSpeakerId(isSpeaking ? peerId : '');
        },
        onDevicesListed: () => {}
      });
    };

    initializeWebRTC();

    const globalPresenceChannel = supabase.channel('global-presence')
      .on('broadcast', { event: 'voice-state-update' }, ({ payload }) => {
        setPeerVoiceStates(prev => ({ 
          ...prev, 
          [payload.userId]: { 
            isMuted: payload.isMuted, 
            isCameraOn: payload.isCameraOn, 
            isScreenSharing: payload.isScreenSharing 
          } 
        }));
      })
      .subscribe();

    const personalChannel = supabase.channel(`user-${currentUser.id}`)
      .on('broadcast', { event: 'dm-call-request' }, ({ payload }) => {
        const sender = contactsRef.current.find(c => c.id === payload.senderId);
        const senderName = sender ? sender.name : 'Unknown Friend';
        const senderAvatar = sender ? sender.avatar : generateMSNAvatar(payload.senderId);
        
        if (import.meta.env.DEV) console.log(`[DM Call] Incoming call from ${senderName} (${payload.senderId})`);
        setDmCallState('ringing');
        setDmCallPeer({ id: payload.senderId, name: senderName, avatar: senderAvatar });
        startRinging(true);
      })
      .on('broadcast', { event: 'dm-incoming-call' }, ({ payload }) => {
        const sender = contactsRef.current.find(c => c.id === payload.senderId);
        const senderName = sender ? sender.name : 'Unknown Friend';
        const senderAvatar = sender ? sender.avatar : generateMSNAvatar(payload.senderId);
        
        if (import.meta.env.DEV) console.log(`[DM Call] Incoming call from ${senderName} (${payload.senderId})`);
        setDmCallState('ringing');
        setDmCallPeer({ id: payload.senderId, name: senderName, avatar: senderAvatar });
        startRinging(true);
      })
      .on('broadcast', { event: 'dm-call-accept' }, ({ payload }) => {
        if (import.meta.env.DEV) console.log(`[DM Call] Call accepted by ${payload.senderId}`);
        stopRinging();
        setDmCallState('idle');
        setDmCallPeer(null);
        setCallChannelId(payload.channelId);
        setIsCallActive(true);
        setCallVaultId(null);
        setCallDuration(0);
        if (callTimerRef.current) clearInterval(callTimerRef.current);
        callTimerRef.current = setInterval(() => { setCallDuration(prev => prev + 1); }, 1000);
        playSound('join');
      })
      .on('broadcast', { event: 'dm-call-accepted' }, ({ payload }) => {
        if (import.meta.env.DEV) console.log(`[DM Call] Call accepted by ${payload.senderId}`);
        stopRinging();
        setDmCallState('idle');
        setDmCallPeer(null);
        setCallChannelId(payload.channelId);
        setIsCallActive(true);
        setCallVaultId(null);
        setCallDuration(0);
        if (callTimerRef.current) clearInterval(callTimerRef.current);
        callTimerRef.current = setInterval(() => { setCallDuration(prev => prev + 1); }, 1000);
        playSound('join');
      })
      .on('broadcast', { event: 'dm-call-decline' }, ({ payload }) => {
        if (import.meta.env.DEV) console.log(`[DM Call] Call declined by ${payload.senderId}`);
        stopRinging();
        setDmCallState('idle');
        setDmCallPeer(null);
        addNotification({ type: 'system', title: 'Call Declined', message: 'The user declined your call request.' });
      })
      .on('broadcast', { event: 'dm-call-declined' }, ({ payload }) => {
        if (import.meta.env.DEV) console.log(`[DM Call] Call declined by ${payload.senderId}`);
        stopRinging();
        setDmCallState('idle');
        setDmCallPeer(null);
        addNotification({ type: 'system', title: 'Call Declined', message: 'The user declined your call request.' });
      })
      .on('broadcast', { event: 'dm-call-cancel' }, ({ payload }) => {
        if (import.meta.env.DEV) console.log(`[DM Call] Call cancelled by caller ${payload.senderId}`);
        stopRinging();
        setDmCallState('idle');
        setDmCallPeer(null);
      })
      .on('broadcast', { event: 'dm-call-cancelled' }, ({ payload }) => {
        if (import.meta.env.DEV) console.log(`[DM Call] Call cancelled by caller ${payload.senderId}`);
        stopRinging();
        setDmCallState('idle');
        setDmCallPeer(null);
      })
      .on('broadcast', { event: 'force-mute' }, ({ payload }) => {
        if (import.meta.env.DEV) console.log(`[Moderation] Force muted by admin ${payload.adminId}`);
        setIsMuted(true);
        addNotification({ type: 'system', title: 'Server Muted', message: 'A server admin has muted your microphone.' });
      })
      .on('broadcast', { event: 'invite-to-vault' }, ({ payload }) => {
        if (import.meta.env.DEV) console.log(`[Server] Received invite to ${payload.vaultName} from ${payload.senderId}`);
        if (currentUser?.id !== payload.senderId) {
          addNotification({ 
          type: 'system', 
          title: 'Server Invite', 
          message: `You were invited to join ${payload.vaultName}`,
          action: () => handleJoinVault(payload.vault)
        });
        }
      })
      .on('broadcast', { event: 'vault-invite' }, ({ payload }) => {
        if (import.meta.env.DEV) console.log(`[Server] Received invite to ${payload.vaultName} from ${payload.senderId}`);
        if (currentUser?.id !== payload.senderId) {
          addNotification({ 
          type: 'system', 
          title: 'Server Invite', 
          message: `You were invited to join ${payload.vaultName}`,
          action: () => handleJoinVault(payload.vault)
        });
        }
      })
      .on('broadcast', { event: 'vault-kicked' }, ({ payload }) => {
        if (payload.vaultId === activeVault?.id) {
          setActiveVault(null);
          addNotification({ 
          type: 'system', 
          title: 'Removed from Server', 
          message: 'You have been removed from the server.',
        });
        }
      })
      .subscribe();

    return () => {
      globalPresenceChannel.unsubscribe();
      personalChannel.unsubscribe();
      stopRinging();
      if (webRTCManagerRef.current) {
        webRTCManagerRef.current.disconnectAll();
      }
    };
  }, [currentUser?.id]);

  // Broadcast local voice state to peers whenever it changes
  useEffect(() => {
    if (!isCallActive) return;

    // `currentGame` / `currentGameSessionId` drive the 🎮 pill that other
    // voice-channel participants see next to a peer's name when they're
    // playing a voice-channel activity (FreeDoom, future Snake/Chess). The
    // session id is a stable id tied to the active game's lifetime — when
    // `activeGame` flips from non-null to null we null both so peers' rails
    // decay cleanly when the activity exits.
    sendBroadcast('global-presence', 'voice-state-update', {
      userId: currentUser?.id,
      isMuted,
      isCameraOn: isVideoOn,
      isScreenSharing,
      isWatchTogetherHost,
      currentGame: activeGame ?? null,
      currentGameSessionId: activeGame ? `local-${activeGame}` : null,
    });
  }, [isMuted, isVideoOn, isScreenSharing, isWatchTogetherHost, isCallActive, currentUser?.id, activeGame]);

  // ----- V2 background-effect lifecycle (canvas pipeline) -----
  // Three split effects avoid the recurring-construction bug the prior
  // round hit (every slider tick on `videoEffectIntensity` would tear
  // down + restart the canvas pipeline, including MediaPipe warmup):
  //
  //   1. MASTER LIFECYCLE — instantiate / tear down the processor. Only
  //      runs when (mode, isVideoOn, localStreamVersion) change.
  //   2. MODE FLIP — push live `videoEffectMode` + `videoEffectBg` to
  //      the running processor via `setMode()`.
  //   3. INTENSITY FLIP — push live `videoEffectIntensity` via
  //      `setIntensity()`. This is the actual "tick without churn"
  //      path; the slider's per-pixel drag no longer rebuilds MediaPipe.
  //
  // The Canvas-driven captureStream track is the ONLY track reachable
  // by `processedStreamRef.current` during the active window — peers
  // see the canvas output because we call `replaceVideoTrack` on every
  // peer connection's video sender as soon as the first composite
  // frame lands. The (already-processed) audio track from
  // AudioProcessor is preserved through the swap so audio enhancements
  // (limiter/normalizer/noise suppression) survive the V2 transition.
  const videoEffectMode      = useCallStore((s) => s.videoEffectMode);
  const videoEffectBg        = useCallStore((s) => s.videoEffectBg);
  const videoEffectIntensity = useCallStore((s) => s.videoEffectIntensity);

  // Effect 1 — master lifecycle.  (kind of counter-intuitive: while
  // most of the work happens on the underlying `useEffect`, it is what
  // keeps the canvas pipeline running. We deliberately run on exactly
  // three signals so an intensity slider drag does NOT enter this
  // path; that lives in Effect 3 below.)
  useEffect(() => {
    const wantProcessing = videoEffectMode !== 'none' && isVideoOn;
    const sourceStream   = localStreamRef.current;

    if (!wantProcessing) {
      // Tear back down — mode flipped off, or camera was turned off.
      if (videoProcessorRef.current) {
        videoProcessorRef.current.stop();
        videoProcessorRef.current = null;
      }
      // Restore peers onto the raw camera track. replaceVideoTrack(null)
      // is treated as a "missing track" signal in some peers, so we
      // explicitly restore the cached camera track from webRTCManager.
      try { webRTCManagerRef.current?.restoreCameraTrack?.(); } catch {}
      // Rebuild processedStreamRef to the audio-processor + raw-camera
      // baseline so the local UI tile still shows the unprocessed feed.
      if (sourceStream) {
        const audioTrack = audioProcessorRef.current?.getProcessedStream?.()?.getAudioTracks?.()[0]
          ?? sourceStream.getAudioTracks()[0];
        const videoTrack = sourceStream.getVideoTracks()[0];
        if (videoTrack) {
          processedStreamRef.current = new MediaStream([videoTrack, audioTrack]);
          setLocalStreamVersion(v => v + 1);
        } else {
          processedStreamRef.current = null;
        }
      }
      return;
    }

    if (!sourceStream) {
      // Camera on intent, but no stream yet — wait for next render.
      return;
    }

    // Guard: if a processor is already running, don't create another one.
    // This prevents the cascade where onFirstCompositeReady calls
    // setLocalStreamVersion which triggers a re-render that would re-run
    // this effect and spawn a new processor feeding on the first one's
    // already-processed output (infinite loop → black video).
    if (videoProcessorRef.current) return;

    // Spawn the canvas pipeline. Default the missing bgSrc to the first
    // preloaded background so the user can't pick 'picture' without a
    // sensible default compositing target.
    const proc = new VideoProcessor({
      sourceStream,
      mode: videoEffectMode,
      bgSrc: videoEffectBg ?? DEFAULT_BACKGROUNDS[0].src,
      intensity: videoEffectIntensity,
      onFirstCompositeReady: (track) => {
        // -------- peers see this track --------
        try { webRTCManagerRef.current?.replaceVideoTrack?.(track); } catch (e) {
          console.warn('[V2 background] replaceVideoTrack failed', e);
        }
        // -------- local tile reads from processedStreamRef --------
        const audioTrack = audioProcessorRef.current?.getProcessedStream?.()?.getAudioTracks?.()[0]
          ?? sourceStream.getAudioTracks()[0];
        processedStreamRef.current = new MediaStream([track, audioTrack]);
        setLocalStreamVersion(v => v + 1); // nudge dependent components to re-render
      },
    });
    videoProcessorRef.current = proc;
    proc.start().catch((e) => {
      console.warn('[V2 background] VideoProcessor.start failed; reverting to raw camera.', e);
      videoProcessorRef.current = null;
      // Tell the user the effect didn't take, and try to put peers back
      // on the camera track via webRTCManager.
      try { webRTCManagerRef.current?.restoreCameraTrack?.(); } catch {}
      addNotification({
        type: 'system',
        title: 'Background effect unavailable',
        message: 'Your browser could not start the background pipeline — peers still see your real feed.',
      });
    });

    return () => {
      proc.stop();
      videoProcessorRef.current = null;
      try { webRTCManagerRef.current?.restoreCameraTrack?.(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mode/bg/intensity
    // are pushed via Effects 2/3 below; including them here would tear the
    // pipeline down on every slider tick (the bug the split avoids).
    // localStreamVersion is deliberately excluded: its bumps from
    // onFirstCompositeReady would cascade into new VideoProcessor creation
    // (cleanup stops old processor → new effect creates another → infinite
    // loop). Mode and camera-on/off are the only real lifecycle triggers.
  }, [videoEffectMode, isVideoOn]);

  // Effect 2 — push mode + bgSrc into the running instance.
  useEffect(() => {
    const proc = videoProcessorRef.current;
    if (!proc) return;
    proc.setMode(videoEffectMode, videoEffectBg ?? null);
  }, [videoEffectMode, videoEffectBg]);

  // Effect 3 — push intensity into the running instance (no churn).
  useEffect(() => {
    const proc = videoProcessorRef.current;
    if (!proc) return;
    proc.setIntensity(videoEffectIntensity);
  }, [videoEffectIntensity]);

  // Effect 4 — debounced writeback of the user's last video-effect
  // choice to profile.customization. Defense A (hydrated-ref) prevents
  // the classic hydration-vs-save race:
  //   - If `videoEffectHydratedRef.current === false`, early-return so
  //     a freshly mounted defaults-only callStore never overwrites the
  //     user's prior DB row.
  //   - If current values match `lastSavedVideoFxRef`, skip no-op
  //     writes (callStore.dirty did not move).
  //   - Otherwise schedule a 600ms-debounced updateProfile() so slider
  //     drag bursts collapse into a single round-trip.
  // The merge with `userProfile.customization` is critical — updateProfile
  // spreads ONLY `patch.customization` into the JSONB blob, so omitting
  // the existing keys would wipe font/palette/bannerType/etc.
  useEffect(() => {
    if (!isAuthenticated || !currentUser?.id) return;
    if (!videoEffectHydratedRef.current) return;

    const last = lastSavedVideoFxRef.current;
    if (last
        && last.mode      === videoEffectMode
        && last.bg        === videoEffectBg
        && last.intensity === videoEffectIntensity) {
      return;
    }
    const snapshot = {
      mode: videoEffectMode,
      bg: videoEffectBg,
      intensity: videoEffectIntensity,
    };
    if (videoFxSaveTimerRef.current) clearTimeout(videoFxSaveTimerRef.current);
    videoFxSaveTimerRef.current = setTimeout(() => {
      videoFxSaveTimerRef.current = null;
      // Read the latest userProfile from the ref (NOT the closure) so a
      // ProfileCustomizer save that landed during the 600ms debounce
      // window isn't overwritten. updateProfile() REPLACES (not merges)
      // the JSONB column, so the spread of existing keys is mandatory.
      const latest = userProfileRef.current;
      const existing = (latest.customization && typeof latest.customization === 'object')
        ? latest.customization
        : {};
      updateProfile(currentUser.id, {
        customization: {
          ...existing,
          videoEffectMode:     snapshot.mode,
          videoEffectBg:       snapshot.bg,
          videoEffectIntensity: snapshot.intensity,
        },
      }).catch((err) => {
        console.warn('[VideoEffect] persist failed', err);
      });
      lastSavedVideoFxRef.current = snapshot;
    }, 600);

    // Empty cleanup is INTENTIONAL: useEffect's cleanup runs on BOTH
    // unmount AND dep change. On dep change the new effect body below
    // already calls clearTimeout() and schedules a fresh timer, so the
    // effect-level cleanup would be redundant. On UNMOUNT we DELIBERATELY
    // let the in-flight timer fire (commit the user's last write) rather
    // than dropping it — otherwise navigating away within 600ms of a
    // change would silently lose the persistence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoEffectMode, videoEffectBg, videoEffectIntensity, isAuthenticated, currentUser?.id]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) return;
      try {
        const { data: profile, error: profileError } = await retryQuery(() =>
          supabase
            .from('profiles')
            .select('id, display_name, photo_url, customization, bio, presence')
            .eq('id', currentUser.id)
            .maybeSingle()
        );

        if (profileError) {
          // Do NOT fall through to the "create new profile" branch below —
          // that branch calls updateProfile() with bare defaults, which
          // would silently clobber an existing user's real profile/
          // customization on a transient error or RLS misconfiguration.
          console.error("Profile fetch failed (RLS or query error):", profileError.message);
          return;
        }

        if (profile) {
          // currentUser.user_metadata reads throughout this file (here and
          // below) are DISPLAY-ONLY fallbacks — never used for
          // authorization. It's user-editable via the Supabase auth API and
          // must never gate a permission/role decision (§16.6).
          setUserProfile(prev => ({
            ...prev,
            name: profile.display_name || currentUser.user_metadata.full_name || 'Guest',
            avatar: profile.photo_url || currentUser.user_metadata.avatar_url || prev.avatar,
            status: profile.status || prev.status,
            bio: profile.bio || prev.bio,
            customization: profile.customization || prev.customization,
            bannerType: profile.customization?.bannerType || prev.bannerType,
            bannerValue: profile.customization?.bannerValue || prev.bannerValue,
            gradientHue1: profile.customization?.gradientHue1 ?? prev.gradientHue1,
            gradientSat1: profile.customization?.gradientSat1 ?? prev.gradientSat1,
            gradientHue2: profile.customization?.gradientHue2 ?? prev.gradientHue2,
            gradientSat2: profile.customization?.gradientSat2 ?? prev.gradientSat2,
            bannerImage: profile.customization?.bannerImage || prev.bannerImage,
            profileBackgroundType: profile.customization?.profileBackgroundType || prev.profileBackgroundType,
            profileBackgroundValue: profile.customization?.profileBackgroundValue || prev.profileBackgroundValue,
            profileBackgroundGradient: profile.customization?.profileBackgroundGradient || prev.profileBackgroundGradient,
            profileBackgroundImage: profile.customization?.profileBackgroundImage || prev.profileBackgroundImage,
            profileTextStyle: profile.customization?.profileTextStyle || prev.profileTextStyle,
            profileBorderStyle: profile.customization?.profileBorderStyle || prev.profileBorderStyle,
          }));
        } else {
          // Create new profile if it doesn't exist (P0: route through updateProfile
          // so bootstrap rows get the correct role and shape that updateProfile self-RLS expects).
          await updateProfile(currentUser.id, {
            display_name: currentUser.user_metadata.full_name || `User_${currentUser.id.slice(0, 5)}`,
            photo_url: currentUser.user_metadata.avatar_url || generateMSNAvatar(currentUser.id),
            status: 'Online',
            role: 'user',
          });
          setUserProfile(prev => ({
            ...prev,
            name: currentUser.user_metadata.full_name || `User_${currentUser.id.slice(0, 5)}`,
            avatar: currentUser.user_metadata.avatar_url || generateMSNAvatar(currentUser.id),
            status: 'Online',
            bio: ''
          }));

          // ---- Hydrate V2 video-effect prefs from profile.customization ----
          // Only apply DB values when the callStore is still sitting at its
          // initial defaults ('none'/null/8). If the user already clicked a
          // mode/bg/intensity BEFORE fetchProfile resolved, respect their
          // in-flight pick — never overwrite a real interaction with stale
          // DB data (the hydration-vs-save race the thinker flagged).
          // We read via useCallStore.getState() because React subscriptions
          // don't see synchronous writes from inside a Promise resolution.
          const liveFx = useCallStore.getState();
          const fxAtDefaults =
            liveFx.videoEffectMode      === 'none' &&
            liveFx.videoEffectBg        === null  &&
            liveFx.videoEffectIntensity === 8;
          const cust = (profile?.customization ?? {}) as any;
          if (fxAtDefaults) {
            // Hydration fan-out: these synchronous setVideoEffect* calls
            // notify the App-level useCallStore selectors, so Effects 1/2/3
            // (videoEffectMode/intensity-driven) WILL re-run with the
            // hydrated values. They are guarded by `!sourceStream` (Effect 1)
            // and `!proc` (Effects 2/3), so the camera-off / no-processor
            // early-returns keep this a no-op until the user actually turns
            // the camera on. No MediaPipe is spun up at boot.
            if (cust.videoEffectMode === 'none'  ||
                cust.videoEffectMode === 'blur'  ||
                cust.videoEffectMode === 'picture') {
              liveFx.setVideoEffectMode(cust.videoEffectMode);
            }
            if (cust.videoEffectBg !== undefined) {
              liveFx.setVideoEffectBg(cust.videoEffectBg ?? null);
            }
            if (typeof cust.videoEffectIntensity === 'number') {
              liveFx.setVideoEffectIntensity(cust.videoEffectIntensity);
            }
          }
          // Seed the diff guard to whatever callStore ended at — what we
          // just read/write to DB. The save effect's first run after
          // hydration is a no-op because lastSaved === current.
          const fxAfter = useCallStore.getState();
          lastSavedVideoFxRef.current = {
            mode:     fxAfter.videoEffectMode,
            bg:       fxAfter.videoEffectBg,
            intensity: fxAfter.videoEffectIntensity,
          };
          videoEffectHydratedRef.current = true;
        }
      } catch (err) {
        console.error("Profile fetch error:", err);
      }
    };

    if (isAuthenticated) {
      fetchProfile();
    }
  }, [isAuthenticated, currentUser?.id]);

  useEffect(() => {
    if (!activeVault || !currentUser) {
      setServerRoles([]);
      setMemberRoles([]);
      return;
    }
    const fetchRoles = async () => {
      try {
        const { data: roles, error: rolesError } = await supabase
          .from('server_roles')
          .select('id, name, position, vault_id')
          .eq('vault_id', activeVault.id)
          .order('position', { ascending: true });
        
        if (rolesError) {
          console.warn("Could not fetch server_roles (migration may not be applied yet):", rolesError.message);
          return;
        }

        const { data: mRoles, error: mRolesError } = await supabase
          .from('server_member_roles')
          .select('*, server_roles(*)')
          .eq('vault_id', activeVault.id);

        if (mRolesError) {
          console.warn("Could not fetch server_member_roles:", mRolesError.message);
          return;
        }

        setServerRoles(roles || []);
        setMemberRoles(mRoles || []);
      } catch (err) {
        console.error("Roles fetch error:", err);
      }
    };
    fetchRoles();
  }, [activeVault?.id, currentUser?.id]);

  // Sync draft profile when settings modal opens or when the initial userProfile finishes loading
  useEffect(() => {
    if (userProfile.name !== 'User' && draftProfile.name === 'User') {
      setDraftProfile(userProfile);
    }
  }, [userProfile]);

  useEffect(() => {
    if (showUserSettings) {
      setDraftProfile(userProfile);
    }
  }, [showUserSettings]);

  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;
    const uid = currentUser.id;
    let idleTimeout: NodeJS.Timeout;
    let currentPresence: 'Online' | 'Away' | 'Offline' | 'Idle' = 'Offline';

    // Realtime Presence channel (shared across all users for global presence state).
    // No Postgres write — presence updates happen via channel.track() only.
    const presenceChannel = supabase.channel('global-user-presence');

    // Add presence listener BEFORE subscribe
    presenceChannel.on('presence', { event: 'sync' }, () => {
      const state = presenceChannel.presenceState();
      const presenceByUserId = new Map<string, string>();
      Object.keys(state).forEach(key => {
        const presences = state[key] as any[];
        presences.forEach(p => {
          if (p.userId && p.presence) {
            presenceByUserId.set(p.userId, p.presence);
          }
        });
      });
      // Update contacts' presence based on channel state
      setContacts(prev => prev.map(c => {
        const newPresence = presenceByUserId.get(c.id) as ChatContact['presence'] | undefined;
        if (newPresence && newPresence !== c.presence) {
          return {
            ...c,
            presence: newPresence,
            isOnline: newPresence === 'Online' || newPresence === 'Away' || newPresence === 'Idle'
          };
        }
        return c;
      }));
    });

    // Now subscribe after callbacks are registered
    presenceChannel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await presenceChannel.track({
          userId: uid,
          presence: currentPresence
        }).catch(e => console.warn('[Presence] Track failed on subscribe:', e));
      }
    });

    const updatePresence = (presence: 'Online' | 'Away' | 'Offline' | 'Idle') => {
      if (currentPresence === presence) return;
      currentPresence = presence;
      presenceChannel.track({ userId: uid, presence }).catch(e =>
        console.warn('[Presence] Track failed:', e)
      );
      setUserProfile(prev => ({ ...prev, presence }));
    };

    const resetIdle = () => {
      updatePresence('Online');
      clearTimeout(idleTimeout);
      idleTimeout = setTimeout(() => { updatePresence('Idle'); }, 300000);
    };

    let lastActivityAt = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityAt < 10000) return;
      lastActivityAt = now;
      resetIdle();
    };

    let awayTimer: ReturnType<typeof setTimeout> | undefined;
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(awayTimer);
        awayTimer = setTimeout(() => updatePresence('Away'), 15000);
      } else {
        clearTimeout(awayTimer);
        lastActivityAt = 0;
        resetIdle();
      }
    };

    resetIdle();
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimeout(idleTimeout);
      clearTimeout(awayTimer);
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('visibilitychange', handleVisibility);
      presenceChannel.untrack().catch(() => {});
      supabase.removeChannel(presenceChannel);
    };
  }, [isAuthenticated, currentUser?.id]);

  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;

    const fetchProfiles = async () => {
      const { data, error } = await retryQuery(() =>
        supabase
          .from('profiles')
          .select('id, display_name, photo_url')
          .limit(1000)
      );
      if (data) {
        // `vault_id` is a UUID and never embeds the user's id, so the old
        // `.like('vault_id', '%<id>%')` filter never matched anything
        // meaningful. Resolve the user's actual DM vault ids first, then
        // query messages against that set.
        const { data: dmVaultRows } = await supabase
          .from('vaults')
          .select('id')
          .eq('type', 'dm')
          .contains('members', [currentUser.id]);
        const dmVaultIds = (dmVaultRows ?? []).map(v => v.id);

        const { data: unreadMsgs } = dmVaultIds.length > 0
          ? await supabase
              .from('messages')
              .select('sender_id, created_at')
              .in('vault_id', dmVaultIds)
          : { data: [] as { sender_id: string; created_at: string }[] };

        const unreadCounts: Record<string, number> = {};
        if (unreadMsgs) {
          unreadMsgs.forEach(msg => {
            if (msg.sender_id === currentUser.id) return;
            const lastRead = localStorage.getItem(`last_read_dm_${msg.sender_id}`);
            if (!lastRead || new Date(msg.created_at) > new Date(lastRead)) {
              unreadCounts[msg.sender_id] = (unreadCounts[msg.sender_id] || 0) + 1;
            }
          });
        }

        const formattedContacts: ChatContact[] = data.map(u => ({
          id: u.id,
          name: u.display_name || 'Anonymous',
          avatar: u.photo_url || generateMSNAvatar(u.id),
          status: u.status || 'Offline',
          presence: u.presence || 'Offline',
          type: 'human',
          isOnline: u.presence === 'Online' || u.presence === 'Away' || u.presence === 'Idle',
          bio: u.bio || '',
          joinDate: new Date(u.created_at).toLocaleDateString(),
          location: 'Digital Realm',
          customization: u.customization || null,
          bannerType: u.customization?.bannerType,
          bannerValue: u.customization?.bannerValue,
          gradientHue1: u.customization?.gradientHue1,
          gradientSat1: u.customization?.gradientSat1,
          gradientHue2: u.customization?.gradientHue2,
          gradientSat2: u.customization?.gradientSat2,
          bannerImage: u.customization?.bannerImage,
          profileBackgroundType: u.customization?.profileBackgroundType,
          profileBackgroundValue: u.customization?.profileBackgroundValue,
          profileBackgroundGradient: u.customization?.profileBackgroundGradient,
          profileBackgroundImage: u.customization?.profileBackgroundImage,
          profileTextStyle: u.customization?.profileTextStyle,
          profileBorderStyle: u.customization?.profileBorderStyle,
          unreadCount: unreadCounts[u.id] || 0
        }));

        // Bots + safe-contacts union (demo contacts from defaults.ts).
        // CONTACTS in this codebase currently only has type='human' demo
        // entries; both branches land in `safeContacts` so the sidebar
        // is non-empty for fresh accounts on first load.
        const bots = CONTACTS.filter(c => c.type === 'bot');
        const safeContacts = CONTACTS.filter(c => c.type !== 'bot' && (c.type === 'human'));
        const otherRealUsers = formattedContacts.filter(c => c.id !== currentUser.id);
        // A synthetic "self" contact lets the user click SEND DM on their
        // own profile card (matters for the Section 6 self-DM bug).
        const selfContact: ChatContact = {
          id: currentUser.id,
          name: userProfile.name || `${currentUser.email?.split('@')[0] || 'You'} (You)`,
          avatar: userProfile.avatar || generateMSNAvatar(currentUser.id),
          status: 'Notes to self',
          type: 'human',
          presence: 'Online',
          isOnline: true,
          bio: 'A scratchpad only you can see.',
          joinDate: 'Today',
          location: 'Self',
          unreadCount: 0,
        };

        // De-dupe: a real profile row for the demo contacts would otherwise
        // appear twice. We trust the DB row over the static fallback.
        const demoIds = new Set(safeContacts.map(c => c.id));
        const realOthers = otherRealUsers.filter(c => !demoIds.has(c.id));

        setContacts([selfContact, ...realOthers, ...safeContacts, ...bots]);
        setContactsLoading(false);
      } else {
        // Even when the profile fetch fails (offline / RLS hiccup) we
        // want a non-empty sidebar so the user has something to click
        // and isn't staring at a blank Direct Messages panel.
        const selfContact: ChatContact = {
          id: currentUser.id,
          name: userProfile.name || 'You',
          avatar: userProfile.avatar || generateMSNAvatar(currentUser.id),
          status: 'Notes to self',
          type: 'human',
          presence: 'Online',
          isOnline: true,
          unreadCount: 0,
        };
        setContacts([selfContact, ...CONTACTS]);
        setContactsLoading(false);
      }
    };

    fetchProfiles();

    const profileChannel = supabase
      .channel('profiles_all')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, (payload) => {
        // Local patch instead of full `fetchProfiles()` refetch. The old
        // handler re-queried ALL profiles + DM unread counts for EVERY
        // connected client on EVERY profile event (presence flips happen
        // constantly — join/leave/status toggles) — the single biggest
        // realtime egress multiplier in the app. The payload carries the
        // changed row, so patch just that contact in place.
        // NOTE: presence is no longer stored in profiles (moved to Realtime
        // Presence channel) — don't read u.presence here.
        if (payload.eventType === 'UPDATE' && payload.new) {
          const u = payload.new;
          setContacts(prev => prev.map(c =>
            c.id === u.id ? {
              ...c,
              name: u.display_name || 'Anonymous',
              avatar: u.photo_url || generateMSNAvatar(u.id),
              status: u.status || 'Offline',
              bio: u.bio || '',
              customization: u.customization || null,
              bannerType: u.customization?.bannerType,
              bannerValue: u.customization?.bannerValue,
              gradientHue1: u.customization?.gradientHue1,
              gradientSat1: u.customization?.gradientSat1,
              gradientHue2: u.customization?.gradientHue2,
              gradientSat2: u.customization?.gradientSat2,
              bannerImage: u.customization?.bannerImage,
              profileBackgroundType: u.customization?.profileBackgroundType,
              profileBackgroundValue: u.customization?.profileBackgroundValue,
              profileBackgroundGradient: u.customization?.profileBackgroundGradient,
              profileBackgroundImage: u.customization?.profileBackgroundImage,
              profileTextStyle: u.customization?.profileTextStyle,
              profileBorderStyle: u.customization?.profileBorderStyle,
            } : c
          ));
        } else {
          // INSERT / DELETE — rare; a full refresh keeps the list consistent.
          fetchProfiles();
        }
      })
      .subscribe();

    // Presence display channel is now handled in the earlier useEffect (line ~1954)
    // to avoid duplicate subscriptions to 'global-user-presence' channel

    return () => {
      supabase.removeChannel(profileChannel);
    };
  }, [isAuthenticated, currentUser?.id]);

  // Realtime & Presence Sync Hook — memoize the param object so its
  // identity stays stable across renders. The hook's internal useEffects
  // already destructure primitives ([isAuthenticated], [activeVault?.id],
  // etc.) so this is mostly defensive insurance: any future effect that
  // adds the params object itself to its dep array won't cascade.
  const realtimeSyncParams = useMemo(() => ({
    isAuthenticated,
    currentUser,
    userProfile,
    activeVault,
    setActiveVault,
    vaults,
    setVaults,
    setDmRooms,
    setVaultsLoading,
    activeChannelId,
    setChannels,
    categories,
    setCategories,
    isCallActive,
    isCallActiveRef,
    callChannelId,
    isMuted,
    isVideoOn,
    isScreenSharing,
    isWatchTogetherHost,
    setVoiceParticipants,
    voiceParticipants,
    setPeerVoiceStates,
    webRTCManagerRef,
    activeContactId,
    dmRooms,
    setMessages,
    setMessagesLoading,
    setTypingUsers,
    addNotification,
    playSound,
  }), [
    isAuthenticated,
    currentUser?.id,
    userProfile,
    activeVault?.id,
    setActiveVault,
    vaults,
    setVaults,
    setDmRooms,
    setVaultsLoading,
    activeChannelId,
    setChannels,
    categories,
    setCategories,
    isCallActive,
    isCallActiveRef,
    callChannelId,
    isMuted,
    isVideoOn,
    isScreenSharing,
    isWatchTogetherHost,
    setVoiceParticipants,
    voiceParticipants,
    setPeerVoiceStates,
    webRTCManagerRef,
    activeContactId,
    dmRooms,
    setMessages,
    setMessagesLoading,
    setTypingUsers,
    addNotification,
    playSound,
  ]);

  useRealtimeSync(realtimeSyncParams);

  // Request browser notification permission on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'default') {
      window.Notification.requestPermission().then();
    }
  }, []);



  // Join the voice room. No transport here — see services/webRTCManager.ts.
  useEffect(() => {
    if (!isCallActive || !webRTCManagerRef.current || !currentUser || !callChannelId) return;
    
    if (import.meta.env.DEV) console.log(`[WebRTC] Joining room ${callChannelId}`);
    
    const joinVoiceRoom = async () => {
      // Find my roles in the current vault
      const myMemberRoles = memberRoles.filter(mr => mr.user_id === currentUser.id);
      const myRolesList = myMemberRoles.map(mr => mr.server_roles).filter(Boolean);
      
      const hasMic = activeVault ? hasPermission(myRolesList, 'mic_perms') : true;
      const hasVoiceActivity = activeVault ? hasPermission(myRolesList, 'voice_activity') : true;
      
      // If there are no custom roles defined yet, default to having permissions
      const micPermitted = serverRoles.length === 0 ? true : hasMic;
      const voiceActivityPermitted = serverRoles.length === 0 ? true : hasVoiceActivity;

      // Apply restrictions to webRTCManager
      webRTCManagerRef.current?.setPermissionRestrictions(!micPermitted, !voiceActivityPermitted);
      
      if (!micPermitted) {
        setIsMuted(true);
      } else if (!voiceActivityPermitted) {
        // Force PTT initial state
        setIsMuted(true); 
      }

      if (!localStreamRef.current) {
        const rawStream = await webRTCManagerRef.current?.getLocalStream({ audio: true, video: isVideoOn });
        if (rawStream) {
          localStreamRef.current = rawStream;
          webRTCManagerRef.current?.setLocalStream(rawStream);

          // Initialise the audio processor and pipe the raw stream through
          // the noise-suppression / limiter / normalizer chain.
          if (!audioProcessorRef.current) {
            try {
              const proc = new AudioProcessor();
              audioProcessorRef.current = proc;
              processedStreamRef.current = proc.processStream(rawStream);
            } catch (err) {
              console.warn('[AudioProcessor] Failed to initialise:', err);
            }
          }
        }
      }
      await webRTCManagerRef.current?.joinRoom(callChannelId);
    };
    joinVoiceRoom();
  }, [isCallActive, callChannelId, currentUser?.id, memberRoles, serverRoles, activeVault]);

  // Global Push-to-Talk keyboard handlers
  useEffect(() => {
    if (!isCallActive || !webRTCManagerRef.current) return;
    
    // Check if voice activity is restricted
    const myMemberRoles = memberRoles.filter(mr => mr.user_id === currentUser?.id);
    const myRolesList = myMemberRoles.map(mr => mr.server_roles).filter(Boolean);
    const hasVoiceActivity = activeVault ? hasPermission(myRolesList, 'voice_activity') : true;
    const isRestrictedPTT = serverRoles.length > 0 && !hasVoiceActivity;

    if (!isRestrictedPTT && !isPushToTalkEnabled) return;

    const hotkey = pushToTalkHotkey || 'Space';

    const isFocusedOnTextInput = (): boolean => {
      const elem = document.activeElement as any;
      return (
        elem instanceof HTMLInputElement ||
        elem instanceof HTMLTextAreaElement ||
        elem?.contentEditable === 'true'
      );
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const eventKey = e.code === 'Space' ? 'Space' : e.key;
      if ((eventKey === hotkey || e.code === hotkey) && !e.repeat) {
        if (eventKey === 'Space' && !isFocusedOnTextInput()) e.preventDefault();
        webRTCManagerRef.current?.setPTTActive(true);
        setIsMuted(false);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const eventKey = e.code === 'Space' ? 'Space' : e.key;
      if (eventKey === hotkey || e.code === hotkey) {
        if (eventKey === 'Space' && !isFocusedOnTextInput()) e.preventDefault();
        webRTCManagerRef.current?.setPTTActive(false);
        setIsMuted(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isCallActive, isPushToTalkEnabled, pushToTalkHotkey, memberRoles, serverRoles, activeVault, currentUser?.id]);

  // Start or end a call when the user enters or leaves a voice channel.
  useEffect(() => {
    if (callChannelId) {
      setCallDuration(0);
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      callTimerRef.current = setInterval(() => { setCallDuration(prev => prev + 1); }, 1000);
    } else if (isCallActive) {
      endCall();
    }
  }, [callChannelId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Shift') setIsShiftHeld(true); };
    const handleKeyUp = (e: KeyboardEvent) => { if (e.key === 'Shift') setIsShiftHeld(false); };
    window.addEventListener('keydown', handleKeyDown); window.addEventListener('keyup', handleKeyUp);
    return () => { window.removeEventListener('keydown', handleKeyDown); window.removeEventListener('keyup', handleKeyUp); };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (lfoControlsRef.current && !lfoControlsRef.current.contains(event.target as Node)) setShowLfoControls(false);
      if (gradientControlsRef.current && !gradientControlsRef.current.contains(event.target as Node)) setShowGradientControls(false);
      if (uiConfigRef.current && !uiConfigRef.current.contains(event.target as Node)) setShowUiConfig(false);
      if (profileSettingsRef.current && !profileSettingsRef.current.contains(event.target as Node)) setShowProfileSettings(false);
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) setContextMenu(prev => ({ ...prev, visible: false }));
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (sloganRef.current) {
      const fx = new TextScramble(sloganRef.current);
      let counter = 0;
      const phrases = ['PSYDECHAT ACTIVE', 'THE NEURAL SIDE', 'SECURE MESSAGING', 'ZERO TRACE'];
      const next = () => { fx.setText(phrases[counter]).then(() => { setTimeout(next, 2000); }); counter = (counter + 1) % phrases.length; };
      next();
    }
  }, []);

  // Mirror user-controlled contrast/saturation; --h is owned by the LFO drive below.
  useEffect(() => {
    document.documentElement.style.setProperty('--contrast', `${contrast}%`);
    document.documentElement.style.setProperty('--saturation', `${saturation}%`);
  }, [contrast, saturation]);

  // LFO drive — writes the animated hue directly to `--h` via DOM and
  // never touches React state, so 60Hz animation does NOT re-render App.
  // The early-return branch writes baseHue when not animating, so user
  // slider changes are also reflected even when LFO is idle.
  useEffect(() => {
    const writeHue = (v: number) => document.documentElement.style.setProperty('--h', v.toString());
    const isLfoTheme = bgTheme === 'synthwave' || bgTheme === 'aurora';
    const shouldModulate = isLfoTheme || lfoActive;

    if (!shouldModulate) {
      writeHue(baseHue);
      cancelAnimationFrame(lfoFrameRef.current);
      return;
    }

    let animationFrameId: number;
    const updateLFO = () => {
      const now = Date.now() / 1000;
      let nextHue: number;

      if (lfoActive && !isLfoTheme) {
        const t = now * lfoSpeed;
        if (lfoShape === 'sine') nextHue = Math.abs(Math.sin(t) * 360);
        else if (lfoShape === 'triangle') nextHue = Math.abs((t % 2 - 1) * 360);
        else if (lfoShape === 'square') nextHue = Math.floor(t % 2) * 180 + 90;
        else nextHue = baseHue;
      } else {
        const cycleSpeed = (2 * Math.PI) / 12;
        const depth = 8;
        nextHue = baseHue + Math.sin(now * cycleSpeed) * depth;
      }

      writeHue(nextHue);
      animationFrameId = requestAnimationFrame(updateLFO);
    };

    animationFrameId = requestAnimationFrame(updateLFO);
    lfoFrameRef.current = animationFrameId;
    return () => cancelAnimationFrame(animationFrameId);
  }, [baseHue, bgTheme, lfoActive, lfoSpeed, lfoShape]);

  useEffect(() => {
    if (vibrant) document.body.classList.add('vibrant-mode'); else document.body.classList.remove('vibrant-mode');
    return () => document.body.classList.remove('vibrant-mode');
  }, [vibrant]);

  useEffect(() => {
    // Add `theme-synthwave` explicitly when bgTheme='synthwave' so that
    // Synthwave-scoped CSS rules can target `body.theme-synthwave` directly
    // instead of relying on `body:not(.theme-X):not(.theme-Y):…` selector
    // games. The cleanup phase strips every theme class including
    // `theme-synthwave` so a route change never leaves a stale class behind.
    const themes = ['theme-synthwave', 'theme-aurora', 'theme-day', 'theme-ocean', 'theme-classic', 'theme-aeroform', 'theme-monolith', 'theme-catppuccin', 'theme-tokyonight'];
    document.body.classList.remove(...themes);
    document.body.classList.add(`theme-${bgTheme}`);
    return () => document.body.classList.remove(...themes);
  }, [bgTheme]);

  useEffect(() => {
    const fetchEmotes = async () => {
      try {
        // Fetch emotes from the Stewie-J/EMOTES repo (flat structure — all images at root)
        const response = await fetch('https://api.github.com/repos/Stewie-J/EMOTES/contents/?ref=main');
        if (response.ok) {
          const data: any = await response.json();
          const emotes = data.filter((file: any) => file.type === 'file' && /\.(png|jpg|jpeg|gif|webp)$/i.test(file.name)).map((file: any) => ({ name: file.name.split('.')[0], url: file.download_url }));
          if (emotes.length > 0) setCustomEmotes(emotes);
          else setCustomEmotes([{ name: 'smile', url: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f600.png' }, { name: 'grin', url: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f601.png' }, { name: 'joy', url: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f602.png' }]);
        } else { setCustomEmotes([{ name: 'bot1', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=1' }, { name: 'bot2', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=2' }, { name: 'bot3', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=3' }, { name: 'bot4', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=4' }]); }
      } catch (error) { setCustomEmotes([{ name: 'bot1', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=1' }, { name: 'bot2', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=2' }, { name: 'bot3', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=3' }, { name: 'bot4', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=4' }]); }
    };
    fetchEmotes();
  }, []);

  useEffect(() => {
    if (gradientMode) document.body.classList.add('gradient-mode'); else document.body.classList.remove('gradient-mode');
    return () => document.body.classList.remove('gradient-mode');
  }, [gradientMode]);

  useEffect(() => { if (localStreamRef.current) { localStreamRef.current.getAudioTracks().forEach(track => { track.enabled = !isMuted; }); } }, [isMuted]);

  useEffect(() => {
    webRTCManagerRef.current?.setDeafened(isDeafened);
  }, [isDeafened]);

  useEffect(() => {
    if (audioProcessorRef.current) {
      audioProcessorRef.current.setNormalizeThreshold(isNormalizerEnabled ? normalizerThreshold : 0);
      audioProcessorRef.current.setLimiterThreshold(isLimiterEnabled ? limiterThreshold : 0);
      audioProcessorRef.current.setOutputGain(outputGain);
      audioProcessorRef.current.setNoiseSuppressionEnabled(isNoiseSuppressionEnabled);
    }
  }, [isNormalizerEnabled, isLimiterEnabled, limiterThreshold, normalizerThreshold, outputGain, isNoiseSuppressionEnabled]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingPip) return;
      const newX = window.innerWidth - e.clientX - pipDragOffset.current.x;
      const newY = window.innerHeight - e.clientY - pipDragOffset.current.y;
      setPipPosition({ x: Math.max(0, Math.min(newX, window.innerWidth - 320)), y: Math.max(0, Math.min(newY, window.innerHeight - 180)) });
    };
    const handleMouseUp = () => setIsDraggingPip(false);
    if (isDraggingPip) { window.addEventListener('mousemove', handleMouseMove); window.addEventListener('mouseup', handleMouseUp); }
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [isDraggingPip]);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target || typeof target.scrollTop !== 'number') return;
      const currentScrollTop = target.scrollTop;
      if (currentScrollTop > lastScrollY.current + 20) {
        // Scrolling down -> collapse
        setIsTopBarExpanded(false);
      } else if (currentScrollTop < lastScrollY.current - 20) {
        // Scrolling up -> expand
        setIsTopBarExpanded(true);
      }
      lastScrollY.current = currentScrollTop;
    };

    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, []);

  useEffect(() => {
    if (isCallActive) {
      if (isVideoOn && localStreamRef.current && localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
      if (isScreenSharing && screenStreamRef.current && screenVideoRef.current) screenVideoRef.current.srcObject = screenStreamRef.current;
    }
  }, [isCallActive, isVideoOn, isScreenSharing, localStreamRef.current, screenStreamRef.current]);

  // PIP (Picture-in-Picture) auto-toggle.
  //
  // Behavior contract:
  //   1. Browsing channels WITHIN THE SAME SERVER as the active call must
  //      NOT trigger PIP. The sidechat content swaps in place while the
  //      call stays embedded.
  //   2. PIP auto-engages only when the user navigates OUT of the call's
  //      contextual surface, specifically:
  //        a) clicking the Global Feed (activeTab === 'feed')
  //        b) clicking the DMs anchor on the left rail (no activeVault)
  //        c) clicking a DIFFERENT server (activeVault.id !== callVaultId)
  //        d) leaving a server-context call toward DMs
  //   3. PIP auto-exits when the user returns to the exact matching
  //      surface (same vault + viewing the original voice channel OR same
  //      DM context for DM calls).
  useEffect(() => {
    if (!isCallActive) return;

    const isViewingFeed = activeTab === 'feed';
    const isViewingDMs = sidebarView === 'dms' || !activeVault;

    const isInSameServerAsCall =
      !!activeVault && !!callVaultId && activeVault.id === callVaultId;
    const isInSameDmAsCall = !callVaultId && !activeVault && !!activeContactId;

    const isViewingCallChannel =
      isInSameServerAsCall && activeChannelId === callChannelId;
    const isViewingDmCall = isInSameDmAsCall; // DM calls: any DM view counts

    const leftServerForAnotherServer =
      !!callVaultId && !!activeVault && activeVault.id !== callVaultId;
    const leftServerForDMs = !!callVaultId && isViewingDMs;
    const leftDmForServer = !callVaultId && !!activeVault && activeVault.id !== null;

    const shouldBePip =
      isViewingFeed ||
      leftServerForAnotherServer ||
      leftServerForDMs ||
      leftDmForServer;

    if (!shouldBePip && isPipMode) {
      // Anything other than the call's contextual surface → drop PIP cleanly.
      // Covers same-server browsing on a non-call channel and return from feed/DMs.
      setIsPipMode(false);
    }
  }, [
    activeChannelId,
    activeContactId,
    activeTab,
    activeVault,
    sidebarView,
    isCallActive,
    callChannelId,
    callVaultId,
    // Intentionally NOT isPipMode: this effect should react to navigation
    // changes (shouldBePip), not to manual PiP toggles. If isPipMode were
    // in the list, clicking the "Return" button would set isPipMode=false,
    // re-fire the effect, and immediately re-enter PiP because shouldBePip
    // is still true (the user hasn't navigated back to the call context
    // yet).
  ]);

  const filteredFeedPosts = useMemo(() => {
    if (feedFilter === 'following') {
      return feedPosts.filter(post => followedUsers.includes(post.authorId));
    }
    if (feedFilter === 'expiring') {
      return feedPosts.filter(post => post.expiresAt !== null);
    }
    return feedPosts;
  }, [feedPosts, feedFilter, followedUsers]);  // endCallRef is wired up via direct render-time assignment right after
  // the `endCall` definition (~line 2876). Placement there avoids the TDZ
  // error that the [endCall] deps form triggered when endCall was
  // referenced before its const initialization, and the useEffect
  // indirection is unnecessary since ref mutations have no render impact.

  const handleJoinVault = async (vault: any, password?: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const uid = user.id;
    const isMember = vault.members?.includes(uid);
    const isCreator = await PermissionService.has({ id: uid }, PERMISSION_MANAGE_SERVER, { vaultId: vault.id });
    
    const finalizeJoin = (v: any) => {
      // Put it in the sidebar rail immediately. useRealtimeSync's vault
      // subscription will also refetch off the UPDATE, but that round-trip
      // is slower than the user looking at the rail, and a missed realtime
      // event would otherwise leave the server invisible until reload.
      setVaults(prev => (prev.some(existing => existing.id === v.id) ? prev : [v, ...prev]));

      setActiveVault(v);
      setActiveContactId(null);
      setActiveTab('chat');
      setMobileView('sidebar');
      setIsPipMode(false);

      let targetChannel = lastViewedChannels[v.id] || 'general';
      setActiveChannelId(targetChannel);
      setMessages([]);
    };

    if (isMember || isCreator) {
      finalizeJoin(vault);
      return;
    }
    // ---- Vault join (migration 20260917000000_join_vault_rpc) -----------
    // Membership is `vaults.members` (uuid[]) — there is no vault_members
    // table. This used to UPDATE that column straight from the client, but
    // the UPDATE policy on `vaults` is creator-or-admin, so a normal user
    // joining someone else's server matched ZERO rows. PostgREST does not
    // error on a zero-row UPDATE, so the old code saw `error == null`,
    // called finalizeJoin(), and showed the user as joined while the
    // database was untouched — the join silently evaporated on reload.
    //
    // `join_vault` is a SECURITY DEFINER RPC that appends only the calling
    // user to only that column, and enforces the access rules server-side
    // (public = open; anything else must clear verify_vault_access's bcrypt
    // check against the column-revoked password_hash). It returns a boolean
    // rather than throwing on denial, and is idempotent on re-join.
    try {
      const { data: joined, error } = await supabase.rpc('join_vault', {
        p_vault_id: vault.id,
        p_password: password ?? '',
      });

      if (error) {
        // Log the fields individually — a bare `console.error(err)` on a
        // PostgrestError prints "[object Object]" in production consoles,
        // which made the earlier "cannot join" reports impossible to
        // diagnose from a screenshot.
        console.error('join_vault RPC failed:', error.code, error.message, error.details, error.hint);
        addNotification({
          type: 'system',
          title: 'Join Failed',
          message: error.code === 'PGRST202'
            ? 'Server join is not set up on the backend yet (join_vault RPC missing).'
            : `Could not join: ${error.message ?? 'unknown error'}`,
        });
        return;
      }

      if (!joined) {
        // Denied. For a non-public vault that means a wrong/absent
        // password, so re-prompt; a public vault reaching here means the
        // row vanished between listing and joining.
        if (vault.type !== 'public') {
          setVaultToJoin(vault); setShowPasswordModal(true); return;
        }
        addNotification({
          type: 'system',
          title: 'Access Denied',
          message: 'This server is no longer available to join.',
        });
        return;
      }

      const updatedMembers = [...(vault.members || []), uid];
      finalizeJoin({ ...vault, members: updatedMembers });
      setShowPasswordModal(false); setPasswordToJoin(""); setVaultToJoin(null);
    } catch (error) {
      console.error("Critical join error:", error);
      addNotification({ type: 'system', title: 'System Error', message: 'The neural link was interrupted while joining.' });
    }
  };

  /**
   * Open a vault by id, fetching it if it isn't in local state yet (which is
   * the normal case right after redeeming an invite for a server you were
   * never a member of). Shared by the InviteEmbed card and the `?invite=`
   * deep-link handler below.
   */
  const openVaultById = useCallback(async (vaultId: string) => {
    if (!vaultId) return;
    const existing = vaults.find(v => v.id === vaultId);
    if (existing) {
      handleJoinVault(existing);
      return;
    }
    const { data } = await supabase.from('vaults').select('id, name, icon_url, type, description, created_by, members, customization').eq('id', vaultId).maybeSingle();
    if (data) handleJoinVault(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaults]);

  // InviteEmbed lives several components deep (LinkEmbed → FeedPostCard /
  // NeuralMessageBubble), so it announces a successful redeem via a window
  // event rather than a prop callback threaded through all of them.
  useEffect(() => {
    const onInviteJoined = (e: Event) => {
      const vaultId = (e as CustomEvent).detail?.vaultId;
      if (vaultId) void openVaultById(vaultId);
    };
    window.addEventListener(INVITE_JOINED_EVENT, onInviteJoined);
    return () => window.removeEventListener(INVITE_JOINED_EVENT, onInviteJoined);
  }, [openVaultById]);

  // `?invite=CODE` deep link. Redeems once the user is authenticated, then
  // strips the param so a refresh doesn't try to redeem again (harmless —
  // redeem_invite is idempotent for existing members — but it would keep
  // re-navigating away from wherever the user had browsed to).
  const redeemedInviteRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !currentUser || redeemedInviteRef.current) return;
    const code = new URLSearchParams(window.location.search).get('invite');
    if (!code) return;
    redeemedInviteRef.current = true;

    (async () => {
      const { data, error } = await supabase.rpc('redeem_invite', { p_code: code });
      window.history.replaceState({}, '', window.location.pathname);

      if (error) {
        console.error('redeem_invite (deep link) failed:', error.code, error.message);
        addNotification({ type: 'system', title: 'Invite Failed', message: 'Could not redeem that invite link.' });
        return;
      }
      const result = data as { ok: boolean; reason: string; vault_id?: string } | null;
      if (!result?.ok) {
        addNotification({
          type: 'system',
          title: 'Invite Unusable',
          message: result?.reason === 'expired' ? 'That invite has expired.'
            : result?.reason === 'exhausted' ? 'That invite has reached its use limit.'
            : 'That invite is no longer valid.',
        });
        return;
      }
      if (result.vault_id) await openVaultById(result.vault_id);
    })();
  }, [isAuthenticated, currentUser, openVaultById]);

  // --- 4. Early Returns (Conditional Render) ---
  if (isAuthChecking) {
    return (
      <div className="min-h-screen bg-[var(--bg-panel)] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[var(--neon)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage onLoginSuccess={(isNewUser) => {
      if (isNewUser) {
        setShowUserSettings(true);
        setActiveSettingsCategory('User Profile');
        setNotifications(prev => [...prev, {
          id: Date.now().toString(),
          type: 'system',
          title: 'Welcome to Psydechat',
          message: 'Take a moment to setup your profile identity.',
          time: 'Now'
        }]);
      }
    }} />;
  }

  // --- 5. Helper Functions ---
  const handleLogout = async () => {
    try {
      if (currentUser) {
        // Clean up presence in DB before logging out
        await supabase.from('voice_participants').delete().eq('user_id', currentUser.id);
      }
      stopCamera();
      stopScreenShare();
      if (webRTCManagerRef.current) {
        webRTCManagerRef.current.disconnectAll();
      }
      await logout();
    } catch (error) { 
      console.error("Logout failed", error); 
    }
  };

  const toggleInviteUser = (userId: string) => {
    setNewRoomInvitedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };


  const getUserFontStyle = (userId?: string): React.CSSProperties | undefined => {
    if (!userId) return undefined;
    let custom: any = null;
    if (userId === currentUser?.id || userId === 'me') {
      custom = userProfile.customization;
    } else {
      const contact = contacts.find(c => c.id === userId);
      if (contact?.customization) custom = contact.customization;
      else {
        const member = vaultMembers.find(m => m.id === userId);
        if (member?.customization) custom = member.customization;
      }
    }
    if (!custom) return undefined;

    const fontStyle: React.CSSProperties = {};
    if (custom.font && FONT_FAMILIES[custom.font]) {
      fontStyle.fontFamily = FONT_FAMILIES[custom.font];
    }
    const nameEffect = custom.nameStyle || custom.callsignEffect;
    if (nameEffect) {
      const c1 = custom.color1 || custom.themeColor1 || '#818cf8';
      const c2 = custom.color2 || custom.themeColor2 || '#22d3ee';
      const dir = custom.gradientDirection || '90deg';
      Object.assign(fontStyle, getNameStyle(nameEffect, c1, c2, dir));
    }
    return fontStyle;
  };


  const getUserRoleColor = (userId?: string): string | undefined => {
    if (!userId || !activeVault) return undefined;
    const userJunctions = memberRoles.filter(mr => mr.user_id === userId && mr.vault_id === activeVault.id);
    const roles = userJunctions.map(mr => mr.server_roles).filter(Boolean);
    if (roles.length === 0) return undefined;
    const highest = roles.reduce((prev: any, curr: any) => prev.position < curr.position ? prev : curr);
    return highest?.color || undefined;
  };

  function addNotification(notif: Omit<NotificationData, 'id'>) {
    // CSPRNG-derived guest id (was Math.random — predictable). The Date.now()
    // prefix is preserved so message lists (which sort by `id` string) still
    // order newest-last; the random tail kills the predictability.
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? `${Date.now()}-${crypto.randomUUID()}`
        : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setNotifications(prev => [...prev, { ...notif, id }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }

  const handleDeleteVault = async (vaultId: string) => {
    const vault = vaults.find(v => v.id === vaultId);
    setDeleteConfirmation({ vaultId, vaultName: vault?.name || 'Unknown Server' });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmation) return;
    const { vaultId } = deleteConfirmation;
    setDeleteConfirmation(null);

    try {
      // First delete all messages in this vault to avoid foreign key constraints (if any)
      await supabase.from('messages').delete().eq('vault_id', vaultId);
      // Delete the vault itself
      const { error } = await supabase.from('vaults').delete().eq('id', vaultId);
      if (error) throw error;

      setVaults(prev => prev.filter(v => v.id !== vaultId));
      if (activeVault?.id === vaultId) {
        setActiveVault(null);
        setMobileView('sidebar');
      }
      addNotification({ type: 'system', title: 'Server Deleted', message: 'The server has been permanently removed.' });
    } catch (err: any) {
      console.error('Error deleting vault:', err);
      addNotification({ type: 'system', title: 'Error', message: 'Failed to delete the server: ' + err.message });
    }
  };

  const handleClearAllNodes = async () => {
    // P0: gate by DB role (not JWT email) — mirrors handleClearFeed's guard
    // so the security boundary is the SQL policy, not the client-side email.
    if (!adminModeEnabled || !(await isPlatformAdmin(currentUser))) {
      addNotification({ type: 'system', title: 'Permission Denied', message: 'Only the administrator can clear all nodes.' }); return;
    }
    if (!confirm('Are you sure you want to clear all nodes?')) return;
    try {
      // Direct Supabase deletes — the role-based admin DELETE policies
      // (vaults_delete_admin, messages_delete_admin, feed_posts_delete_admin,
      // profiles_delete_admin) authorize these because chimske is role='admin'.
      // The .neq('id', '00000000-0000-0000-0000-000000000000') filter matches
      // every row in the table (it's a UUID sentinel that no real row uses).
      // We previously routed through /api/nodes (server.ts) but that endpoint
      // is not deployed to Cloudflare Pages, so the fetch silently failed and
      // the UI looked cleared while the rows persisted.
      const results = await Promise.all([
        supabase.from('vaults').delete().neq('id', '00000000-0000-0000-0000-000000000000'),
        supabase.from('messages').delete().neq('id', '00000000-0000-0000-0000-000000000000'),
        supabase.from('feed_posts').delete().neq('id', '00000000-0000-0000-0000-000000000000'),
        supabase.from('feed_replies').delete().neq('id', '00000000-0000-0000-0000-000000000000'),
        supabase.from('profiles').delete().eq('role', 'guest'),
      ]);
      const firstError = results.find((r) => r.error)?.error;
      if (firstError) throw firstError;

      // Optimistic local-state clear so the UI updates instantly.
      setVaults([]);
      setDmRooms([]);
      setChannels(DEFAULT_CHANNELS);
      setActiveVault(null);
      setActiveChannelId(null);
      setShowClearNodesConfirm(false);
      addNotification({ type: 'system', title: 'Nodes Cleared', message: `Successfully purged nodes and encrypted links.` });
    } catch (error: any) {
      console.error("Critical error in handleClearAllNodes:", error);
      addNotification({ type: 'system', title: 'Purge Failed', message: error?.message || 'Check console for details.' });
    }
  };

  const handleClearAllGuests = async () => {
    // P0: gate by DB role (not JWT email) — mirrors handleClearAllNodes's guard
    if (!adminModeEnabled || !(await isPlatformAdmin(currentUser))) {
      addNotification({ type: 'system', title: 'Permission Denied', message: 'Only the administrator can clear all guests.' });
      return;
    }

    let count = 0;
    let usedServerApi = false;

    // 1. Try server API preview first if server.ts is running
    try {
      const countRes = await fetch(
        `/api/admin/guests/count?userEmail=${encodeURIComponent(currentUser.email ?? '')}`,
      );
      if (countRes.ok) {
        const parsed = (await countRes.json()) as { count?: number };
        count = Number(parsed.count ?? 0);
        usedServerApi = true;
      }
    } catch {
      // server.ts not running or unreachable — fallback below
    }

    // 2. Direct Supabase query fallback if server API count was unreachable
    if (!usedServerApi) {
      try {
        const { data: rpcCount, error: rpcErr } = await supabase.rpc('count_guest_users');
        if (!rpcErr && rpcCount !== null && rpcCount !== undefined) {
          count = Number(rpcCount);
        } else {
          const { count: profileCount, error: profileErr } = await supabase
            .from('profiles')
            .select('id', { count: 'exact', head: true })
            .eq('role', 'guest');
          if (!profileErr && profileCount !== null && profileCount !== undefined) {
            count = profileCount;
          }
        }
      } catch (err) {
        console.warn('Fallback guest count query failed:', err);
      }
    }

    const confirmation = confirm(
      count === 0
        ? 'There are no guest accounts to delete. Confirm to acknowledge.'
        : `Delete all ${count} guest account(s)? This removes guest profiles and accounts. Any feed_replies authored by guests will also cascade-delete. This is irreversible.`
    );
    if (!confirmation) return;

    try {
      let deleted = count;
      let addedSincePreview: number | null = null;
      let deleteSuccess = false;

      // 3. Try server API deletion if available
      if (usedServerApi) {
        try {
          const res = await fetch('/api/admin/guests', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userEmail: currentUser?.email, previewedCount: count }),
          });
          if (res.ok) {
            const parsed = (await res.json()) as { deleted?: number; addedSincePreview?: number | null };
            deleted = Number(parsed.deleted ?? count);
            addedSincePreview = typeof parsed.addedSincePreview === 'number' ? parsed.addedSincePreview : null;
            deleteSuccess = true;
          }
        } catch {
          deleteSuccess = false;
        }
      }

      // 4. Direct Supabase deletion fallback if server API was not available or failed
      if (!deleteSuccess) {
        const { data: rpcDel, error: rpcDelErr } = await supabase.rpc('delete_guest_users', { preview_count: count });
        if (!rpcDelErr && rpcDel && Array.isArray(rpcDel) && rpcDel[0]) {
          deleted = Number(rpcDel[0].deleted_count ?? count);
        } else {
          const { data: deletedRows, error: dbErr } = await supabase
            .from('profiles')
            .delete()
            .eq('role', 'guest')
            .select('id');
          if (dbErr) throw dbErr;
          deleted = deletedRows?.length ?? count;
        }
      }

      // Delete any DM vaults associated with deleted guest accounts
      try {
        const { data: currentProfiles } = await supabase.from('profiles').select('id');
        const validUserIds = new Set((currentProfiles || []).map(p => p.id));
        const { data: allDmVaults } = await supabase.from('vaults').select('id, members').eq('type', 'dm');
        if (allDmVaults) {
          const orphanDmVaultIds = allDmVaults
            .filter(v => (v.members || []).some((mId: string) => !validUserIds.has(mId)))
            .map(v => v.id);
          if (orphanDmVaultIds.length > 0) {
            await supabase.from('vaults').delete().in('id', orphanDmVaultIds);
          }
        }
      } catch (dmErr) {
        console.warn('Orphan DM vault cleanup warning:', dmErr);
      }

      // Clean up local contacts and DM rooms state
      setContacts(prev => prev.filter(c => !c.name.toLowerCase().startsWith('guest_')));
      setDmRooms(prev => prev.filter(r => (r.members || []).every((mId: string) => !mId.startsWith('guest_'))));

      let toast = `Removed ${deleted} guest account(s) and cleared their DM rooms.`;
      if (deleted === 0) {
        toast = 'No guest accounts to remove.';
      } else if (addedSincePreview !== null && addedSincePreview > 0) {
        toast = `Removed ${deleted} guest account(s) — ${addedSincePreview} signed up between preview and cleanup.`;
      }
      addNotification({
        type: 'system',
        title: 'Guests Cleared',
        message: toast,
      });
    } catch (err) {
      console.error('Critical error in handleClearAllGuests:', err);
      addNotification({ type: 'system', title: 'Purge Failed', message: (err as Error)?.message || 'Check console for details.' });
    }
  };

  const handleCreateChannel = async () => {
    if (!newChannelName.trim() || !activeVault || !currentUser) return;
    try {
      const payload = { 
        name: newChannelName.toLowerCase().replace(/\s+/g, '-'), 
        type: newChannelType, 
        vault_id: activeVault.id
      };
      
      if (editingChannelId) {
        const { error } = await supabase.from('channels').update({ name: payload.name, type: payload.type }).eq('id', editingChannelId);
        if (error) {
          setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Error', message: `Update failed: ${error.message}` }]);
          return;
        }
      } else {
        const { data, error } = await supabase.from('channels').insert(payload).select().single();
        if (error) {
          setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Error', message: `Creation failed: ${error.message}` }]);
          return;
        }
        if (data) {
          // Check if we are currently displaying the mock DEFAULT_CHANNELS
          const isMocked = channels === DEFAULT_CHANNELS || (channels.length === 2 && channels[0].id === 'vc' && channels[1].id === 'general');

          if (isMocked && activeVault.id !== 'global') {
            const defaultChannelsToInsert = DEFAULT_CHANNELS.map(ch => ({
              vault_id: activeVault.id,
              name: ch.name,
              type: ch.type
            }));
            const { data: insertedDefaults } = await supabase.from('channels').insert(defaultChannelsToInsert).select();
            if (insertedDefaults) {
              setChannels([...insertedDefaults, data].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
            } else {
              setChannels([...DEFAULT_CHANNELS, data].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
            }
          } else {
            setChannels(prev => [...prev, data].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
          }
          setActiveChannelId(data.id);
        }
      }
      setNewChannelName(""); setNewChannelType("text"); setEditingChannelId(null); setShowCreateChannelModal(false);
    } catch (error: any) { 
        setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Error', message: error.message || 'Unknown error' }]);
    }
  };

  const openRoomSettings = (vaultId?: string) => {
    const targetId = vaultId || activeVault?.id;
    if (!targetId) return;
    setSettingsVaultId(targetId);
    setShowServerSettingsModal(true);
    setShowVaultDropdown(false);
  };
  
  const openChannelSettings = (channelId: string) => {
    setEditingSettingsChannelId(channelId);
    setShowChannelSettingsModal(true);
  };

  const handleRenameChannel = async (channelId: string, newName: string) => {
    if (!newName.trim()) return;
    try {
      await supabase.from('channels').update({ name: newName.trim() }).eq('id', channelId);
    } catch (e) {
      console.error(e);
    }
  };

  const handleLeaveServer = async (vaultId: string) => {
    if (!currentUser) return;
    if (confirm("Are you sure you want to leave this server?")) {
      // Was a DELETE against the nonexistent `vault_members` table, which
      // silently affected nothing — leaving a server never actually left
      // it. `leave_vault` is the SECURITY DEFINER counterpart to
      // `join_vault` (migration 20260917000000) and removes only the
      // calling user from `vaults.members`.
      const { error } = await supabase.rpc('leave_vault', { p_vault_id: vaultId });
      if (error) {
        console.error('leave_vault RPC failed:', error);
        addNotification({
          type: 'system',
          title: 'Leave Failed',
          message: 'Could not leave the server. Try again in a moment.',
        });
        return;
      }
      setVaults(prev => prev.filter(v => v.id !== vaultId));
      setActiveVault(null);
    }
  };

  const handleCreateVault = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!newVaultName.trim() || !user) return;
    try {
      const isCustomIcon = newVaultIcon && (newVaultIcon.startsWith('http') || newVaultIcon.startsWith('/uploads'));
      const defaultIcon = '/psydechat-icon-no-text.png';
      const iconHue = isEditingVault ? (activeVault.icon_hue || 0) : Math.floor(Math.random() * 360);
      
      const vaultData: any = { 
        name: newVaultName, 
        description: newVaultDesc || "No description provided.", 
        icon_url: isCustomIcon ? newVaultIcon : defaultIcon, 
        icon_hue: isCustomIcon ? 0 : iconHue,
        type: newVaultIsPublic ? 'public' : 'private', 
        // NOTE: no `password` key. The plaintext column was replaced by the
        // column-revoked `password_hash` in
        // 20260913000000_hash_vault_passwords.sql, so sending `password`
        // here 400'd the INSERT/UPDATE (the "POST /rest/v1/vaults?select=*"
        // error in the console). Passwords are persisted via the
        // set_vault_password() RPC below (20260921000000).
        members: isEditingVault ? activeVault.members : [user.id], 
        created_by: isEditingVault ? activeVault.created_by : user.id,
      };

      if (isEditingVault) {
        await supabase.from('vaults').update(vaultData).eq('id', activeVault.id);
        // Always sync the password on edit — with an empty password this
        // clears the hash, unlocking the vault (the RPC treats '' as "no
        // password requirement").
        await supabase.rpc('set_vault_password', {
          p_vault_id: activeVault.id,
          p_password: newVaultHasPassword ? newVaultPassword : '',
        });
        setActiveVault({ ...activeVault, ...vaultData });
      } else {
        const { data, error } = await supabase.from('vaults').insert(vaultData).select(VAULT_RETURN_COLUMNS).single();
        if (error) throw error;

        // Persist the join password server-side (column-revoked password_hash
        // cannot be written via the normal insert/update API).
        if (newVaultHasPassword) {
          await supabase.rpc('set_vault_password', {
            p_vault_id: data.id,
            p_password: newVaultPassword,
          });
        }
        
        // Insert default channels for the new vault
        const defaultChannelsToInsert = DEFAULT_CHANNELS.map(ch => ({
          vault_id: data.id,
          name: ch.name,
          type: ch.type
        }));

        const { data: insertedChannels, error: channelsError } = await supabase
          .from('channels')
          .insert(defaultChannelsToInsert)
          .select();

        if (channelsError) {
          console.error("Failed to create default channels:", channelsError);
        }

        setActiveVault(data);

        // Default to the generated 'General' text channel, or fallback
        const generalChannel = insertedChannels?.find(c => c.name === 'General' && c.type === 'text');
        setActiveChannelId(generalChannel ? generalChannel.id : 'general');
        setIsCallActive(false);
        setMobileView('chat');

        // Auto-announce new PUBLIC servers to the Global Feed so other
        // users can discover them, per the "Public Server Discovery"
        // requirement. System-authored post (mirrors the '/psyde' system
        // message pattern) — not a full interactive action-card component,
        // just a discoverable announcement pointing at Browse Public
        // Servers. DM/private vaults never post.
        if (newVaultIsPublic) {
          try {
            const inviteCode = Array.from(crypto.getRandomValues(new Uint8Array(4)))
              .map(b => b.toString(16).padStart(2, '0'))
              .join('')
              .toUpperCase();

            const { error: inviteError } = await supabase.from('server_invites').insert({
              vault_id: data.id,
              code: inviteCode,
              creator_id: user.id,
              max_uses: 0,
            });

            if (inviteError) throw inviteError;

            await supabase.from('feed_posts').insert({
              author_id: user.id,
              author_name: 'SYSTEM',
              author_avatar: '/psydechat-icon-no-text.png',
              content: `🌐 **${newVaultName}** just went public! Open **Browse Public Servers** to check it out and join.\n\nhttps://psyde-chat.pages.dev?invite=${inviteCode}`,
              likes: {},
              reply_count: 0,
            });
          } catch (feedError) {
            console.error('Failed to auto-post new public server to feed:', feedError);
          }
        }
      }

      setNewRoomName(""); setNewRoomDesc(""); setNewRoomIsPublic(true); setNewRoomHasPassword(false); setNewRoomPassword(""); setNewRoomIcon(""); setNewRoomInvitedUsers([]); setIsEditingVault(false); setShowCreateVaultModal(false);
      addNotification({ type: 'system', title: isEditingVault ? 'Server Updated' : 'Server Created', message: isEditingVault ? `${newVaultName} settings updated!` : `Welcome to ${newVaultName}!`, });
    } catch (error) {
      // Previously this was a silent console.error — a failed INSERT (e.g. the
      // vaults column-grant regression that broke server creation) just looked
      // like "clicking Initialize Server does nothing". Surface it so the user
      // can see the failure instead of assuming the modal is broken.
      console.error('Server create/update failed:', error);
      addNotification({
        type: 'system',
        title: isEditingVault ? 'Server Update Failed' : 'Server Creation Failed',
        message: isEditingVault ? 'Could not save server changes.' : 'Could not create the server. Please try again.',
      });
    }
  };

  const toggleMuteUser = (userId: string) => { setMutedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]); setContextMenu(prev => ({ ...prev, visible: false })); };
  const toggleBlockUser = (userId: string) => { setBlockedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]); setContextMenu(prev => ({ ...prev, visible: false })); };
  const handleViewProfile = async (userId: string) => {
    const isSelf = userId === 'me' || userId === currentUser?.id;
    setViewingProfileId(isSelf ? 'me' : userId);
    setContextMenu(prev => ({ ...prev, visible: false }));

    if (isSelf || !currentUser) return;
    
    // Calculate mutual friends and vaults
    try {
      // Mutual Friends
      const { data: otherFriends } = await supabase.from('friends').select('friend_id').eq('user_id', userId);
      const otherFriendsList = (otherFriends || []).map(f => f.friend_id);
      const mutuals = friends.filter(id => otherFriendsList.includes(id));
      setMutualFriends(mutuals);

      // Mutual Vaults
      const { data: otherVaults } = await supabase.from('vaults').select('id').contains('members', [userId]);
      const otherVaultsIds = (otherVaults || []).map(v => v.id);
      const myVaultIds = vaults.map(v => v.id);
      const sharedVaultIds = otherVaultsIds.filter(id => myVaultIds.includes(id));
      const sharedVaults = vaults.filter(v => sharedVaultIds.includes(v.id));
      setMutualVaults(sharedVaults);
    } catch (error) { console.error("Error fetching mutuals:", error); }
  };
  const handleSendMessage = (userId: string, text?: string) => { 
    if (text) {
      sendMessage(undefined, text, { type: 'contact', id: userId });
    }
    handleUnhideDM(userId);
    setMessageRecipientId(userId); 
    setActiveContactId(userId);
    setActiveVault(null);
    setActiveTab('chat');
    setMobileView('chat');
    setViewingProfileId(null);
    setContextMenu(prev => ({ ...prev, visible: false })); 
    if (isCallActive) {
      setIsPipMode(true);
      setShowSideChat(false);
    }
  };
  const handleEmojiClick = (emojiData: EmojiClickData) => { if (pickerMode === 'reaction' && pickerTargetId) { addReaction(pickerTargetId, emojiData.emoji); if (!isShiftHeld) setShowEmojiPicker(false); } };

  const onPickerItemClick = (content: string) => {
    if (pickerMode === 'reaction' && pickerTargetId) { addReaction(pickerTargetId, content); if (!isShiftHeld) setShowEmojiPicker(false); }
    else { const imgMatch = content.match(/!\[([^\]]*)\]\(([^)]*)\)/); if (imgMatch) { const [_, alt, url] = imgMatch; insertHtmlAtCursor(`<img src="${url}" alt="${alt}" data-markdown="${content}" /> `); } else insertHtmlAtCursor(content); if (!isShiftHeld) setShowEmojiPicker(false); }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      const newMessage: Message = { id: Date.now().toString(), text: `![image](${url})`, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), type: 'sent', reactions: {}, expiresAt: Date.now() + expiryTime };
      setChats(prev => ({ ...prev, [activeContactId!]: [...(prev[activeContactId!] || []), newMessage] }));
      setTimeout(() => { if (chatScrollerRef.current) chatScrollerRef.current.scrollTop = chatScrollerRef.current.scrollHeight; }, 10);
    }
  };

  const handleClearFeed = async () => {
    // P0: gate by DB role (not JWT email) - the security boundary lives in SQL.
    if (!adminModeEnabled || !(await isPlatformAdmin(currentUser))) {
      addNotification({ type: 'system', title: 'Permission Denied', message: 'Only the administrator can clear the feed.' }); return;
    }
    if (!confirm('Are you sure you want to clear the entire neural feed?')) return;
    try {
      // Optimistic UI clear.
      setFeedPosts([]);

      // Direct Supabase deletes — the role-based admin DELETE policies
      // (feed_posts_delete_admin, feed_replies_delete_admin) authorize
      // these because chimske is role='admin' (granted by the re-grant
      // migration). The .neq('id', '00000000-0000-0000-0000-000000000000')
      // filter matches every row in the table (it's a UUID sentinel that
      // no real row uses). We previously routed through /api/feed
      // (server.ts) but that endpoint is not deployed to Cloudflare Pages,
      // so the fetch silently failed and the UI looked cleared while the
      // rows persisted.
      const { error: repliesError } = await supabase
        .from('feed_replies')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');
      if (repliesError) throw repliesError;

      const { error: postsError } = await supabase
        .from('feed_posts')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');
      if (postsError) throw postsError;

      if (import.meta.env.DEV) console.log('Feed cleared');
      addNotification({ type: 'system', title: 'Feed Cleared', message: 'All feed posts and replies have been removed.' });
    } catch (error: any) {
      console.error('Error clearing feed:', error);
      // Re-fetch on failure so the UI reflects actual DB state instead of the optimistic empty.
      const { data } = await supabase.from('feed_posts').select('id, author_id, author_name, author_avatar, content, created_at, expires_at, likes, reply_count').limit(50);
      if (data) setFeedPosts(data);
      addNotification({ type: 'system', title: 'Clear Failed', message: error?.message || 'Check console for details.' });
    }
  };

  const handleCreateFeedPost = async (content: string, expiresAt: number | null) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    try {
      const payload: any = {
        author_id: user.id,
        author_name: userProfile.name,
        author_avatar: userProfile.avatar,
        content,
        likes: {},
        reply_count: 0
      };
      if (expiresAt) {
        payload.expires_at = new Date(expiresAt).toISOString();
      }
      await supabase.from('feed_posts').insert(payload);
    } catch (error) { console.error(error); }
  };

  const handleLikeFeedPost = async (postId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const uid = user.id;
    try {
      const { data: post } = await supabase.from('feed_posts').select('likes').eq('id', postId).single();
      if (post) {
        const likes = post.likes || {};
        if (likes[uid]) delete likes[uid];
        else likes[uid] = userProfile.name;
        await supabase.from('feed_posts').update({ likes }).eq('id', postId);
      }
    } catch (error) { console.error(error); }
  };

  const handleReplyFeedPost = async (postId: string, content: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    try {
      // For simplicity, we'll store replies in a separate table or as part of the post
      // In a real app, a 'feed_replies' table is better.
      const { error } = await supabase.from('feed_replies').insert({
        post_id: postId,
        author_id: user.id,
        author_name: userProfile.name,
        author_avatar: userProfile.avatar,
        content
      });
      if (!error) {
        const { data: post } = await supabase.from('feed_posts').select('reply_count').eq('id', postId).single();
        if (post) {
          await supabase.from('feed_posts').update({ reply_count: (post.reply_count || 0) + 1 }).eq('id', postId);
        }
      }
    } catch (error) { console.error(error); }
  };

  const handleDeleteFeedPost = async (postId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    try {
      // Verify ownership before delete
      const { error } = await supabase
        .from('feed_posts')
        .delete()
        .eq('id', postId)
        .eq('author_id', user.id); // RLS-friendly check
      if (error) {
        console.error('Delete failed:', error);
        addNotification({ type: 'system', title: 'Delete Failed', message: 'Could not delete post.' });
      } else {
        // Optimistically remove from local state
        setFeedPosts(prev => prev.filter(p => p.id !== postId));
      }
    } catch (error) { console.error(error); }
  };


  const scrollToMessage = (msgId: string) => { const el = document.getElementById(`msg-${msgId}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); setShowPins(false); };

  const startCall = () => {
    if (activeContactId && !activeVault) {
      const friend = contacts.find(c => c.id === activeContactId);
      if (!friend) return;
      setDmCallState('calling');
      setDmCallPeer({ id: friend.id, name: friend.name, avatar: friend.avatar });
      startRinging(false);
      sendBroadcast(`user-${friend.id}`, 'dm-call-request', { senderId: currentUser?.id });
    } else {
      playSound('join');
      setIsCallActive(true);
      setCallVaultId(activeVault?.id || null);
      setCallDuration(0);
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      callTimerRef.current = setInterval(() => { setCallDuration(prev => prev + 1); }, 1000);
    }
  };

  const endCall = () => {
    playSound('leave');
    // Ensure all cleanup related to UI state is done here,
    // WebRTC connection specific cleanup is done in webRTCManager's onCallEnded handler
    setIsCallActive(false);
    setIsPipMode(false);
    setShowSideChat(false);
    setCallVaultId(null);
    setCallChannelId(null);
    
    if (activeVault) {
      const textChannel = activeVault.channels?.find(c => c.type === 'text') || activeVault.channels?.[0];
      if (textChannel) {
        setActiveChannelId(textChannel.id);
      } else {
        setActiveChannelId('general');
      }
    }
    if (callTimerRef.current) { clearInterval(callTimerRef.current); callTimerRef.current = null; }
    if (audioProcessorRef.current) { audioProcessorRef.current.close(); audioProcessorRef.current = null; }
    processedStreamRef.current = null;
    // V2 background pipeline tear-down — the lifecycle useEffect will
    // own the actual replaceTrack / processedStreamRef rebuild, but we
    // surface the cleanup so the canvas + MediaPipe resources are
    // released on call end (not just lazy-held until the next start).
    if (videoProcessorRef.current) {
      try { videoProcessorRef.current.stop(); } catch { /* ignore */ }
      try { videoProcessorRef.current.releaseHeavyResources?.(); } catch { /* ignore */ }
      videoProcessorRef.current = null;
    }
    stopCamera();
    stopScreenShare();

    // Explicitly clean up voice presence immediately on disconnect
    if (voicePresenceChannelRef.current) {
      voicePresenceChannelRef.current.untrack().catch(() => {});
    }
    setVoiceParticipants(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(chanId => {
        updated[chanId] = (updated[chanId] || []).filter(p => p.id !== currentUser?.id);
      });
      return updated;
    });

    // End specific call or all calls depending on context
    if (webRTCManagerRef.current) {
      if (activeContactId) { // If there's an active direct contact call
        webRTCManagerRef.current.endCall(activeContactId);
      } else { // Otherwise, disconnect all (e.g., for group calls or general cleanup)        webRTCManagerRef.current.disconnectAll();
      }
    }
  };

  // Latest-ref pattern: endCall's identity changes every render; mirror
  // it into the ref so long-lived WebRTC handlers (e.g. onCallEnded)
  // always invoke the current closure instead of a stale one.
  endCallRef.current = endCall;

  const stopCamera = () => {
    if (videoProcessorRef.current) {
      try { videoProcessorRef.current.stop(); } catch { /* ignore */ }
      videoProcessorRef.current = null;
    }
    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      videoTracks.forEach(track => {
        track.stop();
        try { localStreamRef.current?.removeTrack(track); } catch {}
      });
    }
    if (processedStreamRef.current) {
      processedStreamRef.current.getVideoTracks().forEach(track => {
        try { track.stop(); } catch {}
      });
      processedStreamRef.current = null;
    }
    setIsVideoOn(false);
    setLocalStreamVersion(v => v + 1);
  };

  const stopScreenShare = () => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }
    setIsScreenSharing(false);
    if (focusedParticipantId === 'screen') setFocusedParticipantId(activeContact?.id || 'me');

    // Restore camera track via the manager
    if (webRTCManagerRef.current) {
      webRTCManagerRef.current.stopScreenShare();
    }
  };

  const toggleCamera = async () => {
    if (import.meta.env.DEV) console.log('[WebRTC] toggleCamera called, isVideoOn:', isVideoOn);
    if (isVideoOn) {
      playSound('toggle');
      if (webRTCManagerRef.current) {
        webRTCManagerRef.current.toggleVideo(false);
      }
      stopCamera();
    } else {
      try {
        const stream = await acquireCameraStream();
        const newVideoTrack = stream?.getVideoTracks()[0];
        if (!stream || !newVideoTrack) return;

        playSound('toggle');

        // Update localStreamRef.current with the new live video track
        if (localStreamRef.current) {
          localStreamRef.current.getVideoTracks().forEach(t => {
            t.stop();
            try { localStreamRef.current?.removeTrack(t); } catch {}
          });
          localStreamRef.current.addTrack(newVideoTrack);
        } else {
          localStreamRef.current = new MediaStream([newVideoTrack]);
        }

        // Set processedStreamRef to immediate raw stream so UI displays live video tile without black box
        const audioTrack = audioProcessorRef.current?.getProcessedStream?.()?.getAudioTracks?.()[0]
          ?? localStreamRef.current.getAudioTracks()[0];
        processedStreamRef.current = new MediaStream([newVideoTrack, audioTrack]);

        setIsVideoOn(true);

        // Tell WebRTC manager to replace video track on peer connection
        webRTCManagerRef.current?.replaceVideoTrack(newVideoTrack);
        webRTCManagerRef.current?.toggleVideo(true);

        setLocalStreamVersion(v => v + 1);
      } catch (err: any) { 
        console.log('[WebRTC] Failed to get camera', err);
        console.error("Camera access error:", err);
        alert(`Could not access camera: ${err.message || err.name || 'Unknown error'}. Please check permissions and make sure a camera is connected.`); 
      }
    }
  };

  const toggleVideo = () => toggleCamera();

  const toggleMute = () => {
    const newMuted = !isMuted;
    playSound(newMuted ? 'mute' : 'unmute');
    setIsMuted(newMuted);
    if (webRTCManagerRef.current) {
      webRTCManagerRef.current.toggleAudio(!newMuted);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen mode: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const toggleDeafen = () => setIsDeafened(prev => !prev);

  const toggleScreenShare = async (preferWindow: boolean = false) => {
    if (isScreenSharing) { playSound('toggle'); stopScreenShare(); setShowScreenShareOptions(false); }
    else { 
      try { 
        const constraints: any = { video: { cursor: "always" }, audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } }; 
        if (preferWindow) constraints.video.displaySurface = 'window'; 
        const stream = await acquireScreenStream(constraints);
        if (!stream) return;
        playSound('toggle'); 
        screenStreamRef.current = stream; 
        setIsScreenSharing(true); 
        setShowScreenShareOptions(false); 

        // Broadcast screenshare to peers
        if (webRTCManagerRef.current) {
          webRTCManagerRef.current.startScreenShare(stream);
        }

        stream.getVideoTracks()[0].onended = () => stopScreenShare(); 
      } catch (err) { console.error(err); } 
    }
  };
  const insertHtmlAtCursor = (html: string) => {
    if (!richInputRef.current) return; richInputRef.current.focus(); const selection = window.getSelection(); if (!selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0); range.deleteContents(); const el = document.createElement("div"); el.innerHTML = html; const frag = document.createDocumentFragment(); let node; let lastNode; while ((node = el.firstChild)) lastNode = frag.appendChild(node);
    range.insertNode(frag); if (lastNode) { range.setStartAfter(lastNode); range.collapse(true); selection.removeAllRanges(); selection.addRange(range); } setInputValue(getMarkdownFromRichInput());
  };

  const getMarkdownFromRichInput = () => {
    if (!richInputRef.current) return "";
    const parseNode = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
      else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.tagName === 'IMG') return el.getAttribute('data-markdown') || `![image](${el.getAttribute('src')})`;
        else if (el.tagName === 'BR') return "\n";
        else if (el.tagName === 'DIV' || el.tagName === 'P') { let innerMd = ""; el.childNodes.forEach(child => { innerMd += parseNode(child); }); return "\n" + innerMd; }
        else { let innerMd = ""; el.childNodes.forEach(child => { innerMd += parseNode(child); }); return innerMd; }
      }
      return "";
    };
    let markdown = ""; richInputRef.current.childNodes.forEach(node => { markdown += parseNode(node); }); return markdown;
  };

  const formatDuration = (seconds: number) => { const mins = Math.floor(seconds / 60); const secs = seconds % 60; return `${mins}:${secs.toString().padStart(2, '0')}`; };

  const handleTyping = async () => {
    if (!currentUser) return;
    let vaultId: string | null = null;
    if (activeVault) {
      vaultId = activeVault.id;
    } else if (activeContactId) {
      const existingDM = dmRooms.find(r => r.members?.includes(activeContactId));
      if (existingDM) {
        vaultId = existingDM.id;
      }
    }
    
    if (!vaultId) return;
    const now = Date.now();
    if (now - lastTypingTimeRef.current > 3000) { 
      lastTypingTimeRef.current = now; 
      try {
        await supabase.from('typing_indicators').upsert({ vault_id: vaultId, user_id: currentUser.id, is_typing: true, updated_at: new Date().toISOString() });
      } catch (e) { /* Silently fail if table missing */ }
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(async () => { 
      if (!vaultId || !currentUser) return; 
      try {
        await supabase.from('typing_indicators').upsert({ vault_id: vaultId, user_id: currentUser.id, is_typing: false, updated_at: new Date().toISOString() });
      } catch (e) { /* Silently fail if table missing */ }
      lastTypingTimeRef.current = 0; 
    }, 4000);
  };

  const sendMessage = async (e?: React.FormEvent, directText?: string, targetOverride?: { type: 'vault' | 'contact', id: string }) => {
    if (e) e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    let textToSend = directText || getMarkdownFromRichInput();
    
    const completedAttachments = attachments.filter(att => !att.uploading && att.url);
    if (completedAttachments.length > 0) {
      const attachmentMarkdown = completedAttachments.map(att => `![${att.type}](${att.url})`).join('\n');
      textToSend = textToSend ? `${textToSend}\n\n${attachmentMarkdown}` : attachmentMarkdown;
      setAttachments([]);
    }

    if (!textToSend || !textToSend.trim()) return;

    let text = textToSend.trim();
    if (text === '/psyde') {
      if (!directText) {
        setInputValue("");
        if (richInputRef.current) {
          richInputRef.current.innerHTML = "";
          richInputRef.current.blur();
          richInputRef.current.focus();
        }
      }
      const systemMessage: Message = {
        id: `sys-${Date.now()}`,
        text: `\`\`\`
 ██████╗  ██████╗██╗   ██╗██████╗ ███████╗
 ██╔══██╗██╔════╝╚██╗ ██╔╝██╔══██╗██╔════╝
 ██████╔╝╚█████╗   ╚████╔╝ ██║  ██║█████╗  
 ██╔═══╝  ╚═══██╗   ╚██╔╝  ██║  ██║██╔══╝  
 ██║     ██████╔╝    ██║   ██████╔╝███████╗
 ╚═╝     ╚═════╝     ╚═╝   ╚═════╝ ╚══════╝
                                           
[SYSTEM LOGS]
=========================================
NEURAL NET INTERACTION INITIATED...
SECURE LINK STABLE ON FRONTIER.
LOCAL GATEWAY IP: 127.0.0.1
ALL SYSTEMS NOMINAL.
=========================================
\`\`\``,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        type: 'received',
        senderId: 'system',
        senderName: 'SYSTEM',
        senderAvatar: 'https://i.ibb.co/sJ659Yzt/Logo-Uw-U-Invaders.png',
        reactions: {}
      };
      setMessages(prev => [...prev, systemMessage]);
      setTimeout(() => {
        if (chatScrollerRef.current) chatScrollerRef.current.scrollTop = chatScrollerRef.current.scrollHeight;
      }, 50);
      return;
    }

    if (/^(https?:\/\/[^\s]+)$/i.test(text) && /\.(gif|jpe?g|png|webp|bmp)(\?.*)?$/i.test(text) && !text.startsWith('![')) {
      text = `![image](${text})`;
    }

    const currentUserId = user.id;
    let vaultId: string | null = null;
    
    if (targetOverride) {
      if (targetOverride.type === 'vault') {
        vaultId = targetOverride.id;
      } else {
        // DM target override. For a self-DM the recipient equals the
        // sender — dedupe into a single member entry so the vault_id
        // name and Supabase `members` JSON array stay consistent with
        // 2-member DM rooms (this matters for the Section 6 self-DM
        // bug where users could not see their own scratchpad notes).
        const targetId = targetOverride.id;
        const isSelfTarget = targetId === currentUserId;
        const membersPayload = isSelfTarget
          ? [currentUserId]
          : Array.from(new Set([currentUserId, targetId]));
        const existingDM = isSelfTarget
          ? dmRooms.find(r => r.type === 'dm' && (r.members?.length === 1 ? r.members[0] === currentUserId : r.members?.includes(currentUserId) && r.members?.filter((m: string) => m !== currentUserId).length === 0))
          : dmRooms.find(r => r.members?.includes(targetOverride.id) && r.members?.includes(currentUserId));
        if (existingDM) {
          vaultId = existingDM.id;
        } else {
          // Create DM vault on the fly
          const { data: newDM, error } = await supabase.from('vaults').insert({
            name: 'Direct Message',
            type: 'dm',
            members: membersPayload,
            created_by: currentUserId
          }).select(VAULT_RETURN_COLUMNS).single();
          if (newDM) vaultId = newDM.id;
        }
      }
    } else if (activeVault) {
      vaultId = activeVault.id;
    } else if (activeContactId) {
      const isSelfTarget = activeContactId === currentUserId;
      const membersPayload = isSelfTarget
        ? [currentUserId]
        : Array.from(new Set([currentUserId, activeContactId]));
      const existingDM = isSelfTarget
        ? dmRooms.find(r => r.type === 'dm' && (r.members?.length === 1 ? r.members[0] === currentUserId : r.members?.includes(currentUserId) && r.members?.filter((m: string) => m !== currentUserId).length === 0))
        : dmRooms.find(r => r.members?.includes(activeContactId) && r.members?.includes(currentUserId));
      if (existingDM) {
        vaultId = existingDM.id;
      } else {
        // Create DM vault
        const { data: newDM, error } = await supabase.from('vaults').insert({
          name: isSelfTarget ? 'Notes to Self' : 'Direct Message',
          type: 'dm',
          members: membersPayload,
          created_by: currentUserId
        }).select(VAULT_RETURN_COLUMNS).single();
        if (newDM) vaultId = newDM.id;
      }
    }
    
    if (!vaultId) return;
    const channelId = activeChannelId || 'general';

    try {
      const encryptedText = CryptoJS.AES.encrypt(text, vaultId).toString();
      const payload: any = {
        vault_id: vaultId,
        sender_id: currentUserId,
        sender_name: userProfile.name,
        sender_avatar: userProfile.avatar,
        content: encryptedText,
        channel_id: activeVault ? channelId : null,
        reactions: {}
      };

      if (expiryTime > 0) {
        payload.expires_at = new Date(Date.now() + expiryTime).toISOString();
      }

      const { data, error } = await supabase
        .from('messages')
        .insert(payload)
        .select()
        .maybeSingle();

      if (error) throw error;

      // Relay into Discord when this vault is bridged. Fire-and-forget: Discord
      // delivery must never block or fail the local send, which has already
      // succeeded by this point.
      //
      // Sends `text`, not `encryptedText` — messages are AES-encrypted at rest
      // with the vault id as key, and Discord obviously cannot decrypt that.
      // The relay endpoint re-checks vault membership, so this is not the
      // security boundary, just the trigger.
      if (activeDiscordGuildId && channelId) {
        (async () => {
          try {
            const session = await supabase.auth.getSession();
            const token = session?.data?.session?.access_token;
            if (!token) return;

            const relay = await fetch('https://psydechatthing.onrender.com/api/discord/send-message', {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                discord_channel_id: channelId,
                content: text,
                sender_name: userProfile.name,
                sender_avatar: userProfile.avatar,
              }),
            });

            if (!relay.ok) {
              const detail = await relay.json().catch(() => ({}));
              console.warn('Discord relay failed:', relay.status, detail);
            }
          } catch (err) {
            console.warn('Discord relay error:', err);
          }
        })();
      }

      // Optimistic update
      if (data) {
        const optimisticMsg: Message = {
          id: data.id,
          text: text,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          type: 'sent',
          senderId: currentUserId,
          senderName: userProfile.name,
          senderAvatar: userProfile.avatar,
          reactions: {},
          isPending: false
        };
        setMessages(prev => [...prev, optimisticMsg]);
      }

      if (!directText) {
        setInputValue("");
        if (richInputRef.current) {
          richInputRef.current.innerHTML = "";
          richInputRef.current.blur();
          richInputRef.current.focus();
        }
      }

      setLastMessageSentAt(Date.now());
      setTimeout(() => {
        if (chatScrollerRef.current) chatScrollerRef.current.scrollTop = chatScrollerRef.current.scrollHeight;
      }, 50);

      } catch (error: any) {
      console.error('Send failed:', error);
      const errorMessage = error?.message || error?.details || 'Could not connect to the neural network.';
      setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Send Failed', message: errorMessage, time: 'Now' }]); 
      }
    setShowEmojiPicker(false);
  };

  const addReaction = async (msgId: string, emoji: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      await supabase.rpc('increment_reaction', { p_message_id: msgId, p_emoji: emoji });
    } catch (error) {
      console.error('Reaction failed:', error);
    }
  };

  const handleUserContextMenu = (e: React.MouseEvent, userId: string) => {
    e.preventDefault();
    // When the actor has any moderation power in the active vault,
    // the moderation dialog replaces the legacy user-context menu so
    // the two surfaces don't stack on the same anchor.
    if (activeVault && (
      vaultPerms.hasPerm('KICK_MEMBERS') ||
      vaultPerms.hasPerm('BAN_MEMBERS')  ||
      vaultPerms.hasPerm('TIMEOUT_MEMBERS') ||
      vaultPerms.hasPerm('SERVER_MUTE')  ||
      vaultPerms.hasPerm('SERVER_DEAFEN')
    )) {
      setModerationDialog({
        anchor: { x: e.clientX, y: e.clientY },
        targetId: userId,
        targetName: (contacts.find(c => c.id === userId)?.name)
                   ?? (activeVault?.name && userId === currentUser?.id ? userProfile.name : undefined),
      });
      return;
    }
    setContextMenu({ x: e.clientX, y: e.clientY, type: 'user', targetId: userId, visible: true });
  };
  const handleChannelContextMenu = async (e: React.MouseEvent, channelId: string, channelName?: string) => {
    if (!activeVault || !currentUser) return;
    const canManageChannel = await PermissionService.has(currentUser, PERMISSION_EDIT_CHANNELS, { vaultId: activeVault.id, channelId });
    if (!canManageChannel) return;
    e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, type: 'channel', targetId: channelId, targetName: channelName, visible: true });
  };

  const handleSidebarAreaContextMenu = async (e: React.MouseEvent) => {
    if (!activeVault || !currentUser) return;
    const canCreateChannels = await PermissionService.has(currentUser, PERMISSION_CREATE_CHANNELS, { vaultId: activeVault.id });
    if (!canCreateChannels) return;
    if ((e.target as HTMLElement).closest('.channel-item')) return;
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      type: 'channels-sidebar',
      targetId: activeVault.id,
      visible: true
    });
  };

  const handleDeleteChannel = async (channelId: string) => {
    if (!activeVault || !currentUser) return;
    if (channelId === 'general') { setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Error', message: 'Cannot delete the general channel.' }]); return; }
    try { 
      const { error } = await supabase.from('channels').delete().eq('id', channelId);
      if (error) {
        setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Error', message: `Deletion failed: ${error.message}` }]);
        return;
      }
      if (activeChannelId === channelId) setActiveChannelId('general'); 
      setContextMenu(prev => ({ ...prev, visible: false }));
      setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Channel Deleted', message: 'The channel has been removed.' }]);
    } catch (error: any) { 
        setNotifications(prev => [...prev, { id: Date.now().toString(), type: 'system', title: 'Error', message: error.message || 'Unknown error' }]);
    }
  };

  const togglePin = async (msgId: string) => {
    if (!currentUser) return;
    try { 
      const { data: msg } = await supabase.from('messages').select('pinned').eq('id', msgId).single();
      if (msg) {
        await supabase.from('messages').update({ pinned: !msg.pinned }).eq('id', msgId);
      }
    } catch (error) { console.error(error); }
  };

  // --- 6. Main Render Logic ---
  const renderChatInterface = (forcedChannelId?: string) => {
    return (
      <ChatArea
        forcedChannelId={forcedChannelId}
        activeChannelId={activeChannelId}
        channels={channels}
        activeVault={activeVault}
        activeContact={activeContact}
        activeContactId={activeContactId}
        setIsTopBarExpanded={setIsTopBarExpanded}
        isTopBarExpanded={isTopBarExpanded}
        setMobileView={setMobileView}
        setViewingProfileId={setViewingProfileId}
        handleUserContextMenu={handleUserContextMenu}
        getUserFontStyle={getUserFontStyle}
        getUserRoleColor={getUserRoleColor}
        entityResolver={entityResolver}
        migrateLegacyBadge={migrateLegacyBadge}
        mutedUsers={mutedUsers}
        blockedUsers={blockedUsers}
        toggleBlockUser={toggleBlockUser}
        messages={messages}
        setMessages={setMessages}
        attachments={attachments}
        setAttachments={setAttachments}
        startCall={startCall}
        showPins={showPins}
        setShowPins={setShowPins}
        showTimerSettings={showTimerSettings}
        setShowTimerSettings={setShowTimerSettings}
        expiryTime={expiryTime}
        setExpiryTime={setExpiryTime}
        isCallActive={isCallActive}
        showSideChat={showSideChat}
        setShowSideChat={setShowSideChat}
        setActiveVault={setActiveVault}
        scrollToMessage={scrollToMessage}
        chatScrollerRef={chatScrollerRef}
        messagesLoading={messagesLoading}
        displayedMessages={displayedMessages}
        readReceipts={readReceipts}
        addReaction={addReaction}
        setPickerMode={setPickerMode}
        setPickerTargetId={setPickerTargetId}
        showEmojiPicker={showEmojiPicker}
        setShowEmojiPicker={setShowEmojiPicker}
        typingUsers={typingUsers}
        contacts={contacts}
        pickerTab={pickerTab}
        setPickerTab={setPickerTab}
        handleEmojiClick={handleEmojiClick}
        onPickerItemClick={onPickerItemClick}
        emoteSearch={emoteSearch}
        setEmoteSearch={setEmoteSearch}
        customEmotes={customEmotes}
        sendMessage={sendMessage}
        handleFileUpload={handleFileUpload}
        imageInputRef={imageInputRef}
        handleToggleRecording={handleToggleRecording}
        isRecording={isRecording}
        richInputRef={richInputRef}
        setInputValue={setInputValue}
        getMarkdownFromRichInput={getMarkdownFromRichInput}
        handleTyping={handleTyping}
        currentUser={currentUser}
      />
    );
  };

  const handleMuteUserAction = (userId: string) => {
    sendBroadcast(`user-${userId}`, 'force-mute', { adminId: currentUser?.id });
    addNotification({ type: 'system', title: 'User Muted', message: 'You have server-muted the user.' });
    setContextMenu(prev => ({...prev, visible: false}));
  };
  const handleDeafenUserAction = (userId: string) => { /* logic */ setContextMenu(prev => ({...prev, visible: false})); };
  const handleDisconnectUserAction = (userId: string) => { /* logic */ setContextMenu(prev => ({...prev, visible: false})); };

  const handleKickUserAction = async (userId: string) => {
    if (!activeVault || !currentUser) return;
    try {
      const updatedMembers = (activeVault.members || []).filter((id: string) => id !== userId);
      const { error } = await supabase.from('vaults').update({ members: updatedMembers }).eq('id', activeVault.id);
      if (error) throw error;
      
      setActiveVault({ ...activeVault, members: updatedMembers });
      setVaultMembers(prev => prev.filter(m => m.id !== userId));
      setContextMenu(prev => ({ ...prev, visible: false }));
      addNotification({ type: 'system', title: 'User Kicked', message: 'The user was kicked from the server.' });
      sendBroadcast(`user-${userId}`, 'vault-kicked', { vaultId: activeVault.id });
    } catch (error) {
      console.error("Error kicking user:", error);
    }
  };

  const handleBanUserAction = async (userId: string) => {
    if (!activeVault || !currentUser) return;
    try {
      const session = await supabase.auth.getSession();
      const token = session?.data?.session?.access_token;
      if (!token) return;

      const response = await fetch('/api/moderation/ban', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ vaultId: activeVault.id, targetId: userId }),
      });
      if (!response.ok) {
        const data: any = await response.json().catch(() => ({}));
        throw new Error(data?.error || 'Failed to ban user');
      }

      const updatedMembers = (activeVault.members || []).filter((id: string) => id !== userId);
      setActiveVault(prev => ({ ...prev, members: updatedMembers }));
      setVaultMembers(prev => prev.filter(m => m.id !== userId));
      setContextMenu(prev => ({ ...prev, visible: false }));
      addNotification({ type: 'system', title: 'User Banned', message: 'The user was banned from the server.' });
      sendBroadcast(`user-${userId}`, 'vault-kicked', { vaultId: activeVault.id });
    } catch (error) {
      console.error("Error banning user:", error);
    }
  };

  const handleOpenMembersModal = async () => {
    if (!activeVault) return;
    setContextMenu(prev => ({ ...prev, visible: false }));
    setShowMembersModal(true);
    try {
      const memberIds = activeVault.members || [];
      if (memberIds.length === 0) {
        setVaultMembers([]);
        return;
      }
      const { data } = await supabase.from('profiles').select('id, display_name, photo_url').in('id', memberIds);
      if (data) setVaultMembers(data);
    } catch (err) {
      console.error("Error fetching server members:", err);
    }
  };

  const handleCreateInviteLink = async (vaultId: string) => {
    const inviteCode = Array.from(crypto.getRandomValues(new Uint8Array(4)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
      
    const { error } = await supabase.from('server_invites').insert({
      vault_id: vaultId,
      creator_id: currentUser?.id,
      code: inviteCode,
    });
    
    if (!error) {
      const link = `${window.location.origin}?invite=${inviteCode}`;
      try {
        await navigator.clipboard.writeText(link);
        addNotification({ type: 'system', title: 'Invite Link Copied', message: 'Invite link copied to clipboard!' });
      } catch (err) {
        addNotification({ type: 'system', title: 'Invite Created', message: `Link: ${link}` });
      }
    } else {
      addNotification({ type: 'system', title: 'Error', message: 'Could not create invite link.' });
    }
  };

  const myPrivateVaults = vaults.filter(v => v.is_private && v.created_by === currentUser?.id);

  const handleInviteToVault = (userId: string, vaultId: string, vaultName: string) => {
    sendBroadcast(`user-${userId}`, 'invite-to-vault', { senderId: currentUser?.id, vaultId, vaultName });
    addNotification({ type: 'system', title: 'Invite Sent', message: `Invited user to ${vaultName}` });
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const handleReorderChannels = async (reorderedChannels: Channel[]) => {
    setChannels([...reorderedChannels]);
    if (activeVault) {
      const updates = reorderedChannels.map((c) => ({
        id: c.id,
        vault_id: activeVault.id,
        name: c.name,
        type: c.type,
        category_id: c.category_id || null
      }));
      await supabase.from('channels').upsert(updates);
    }
  };

  // --- 6. Main Return (JSX) ---
  return (
    <>
      <ContextMenu
        contextMenu={contextMenu}
        activeVault={activeVault}
        currentUser={currentUser}
        handleMute={handleMuteUserAction}
        handleDeafen={handleDeafenUserAction}
        handleKick={handleKickUserAction}
        handleBan={handleBanUserAction}
        handleDisconnect={handleDisconnectUserAction}
        setContextMenu={setContextMenu}
        handleRenameChannel={handleRenameChannel}
        openChannelSettings={openChannelSettings}
        handleLeaveServer={handleLeaveServer}
        handleRenameCategory={async (categoryId, newName) => {
          await supabase.from('categories').update({ name: newName }).eq('id', categoryId);
        }}
        openCategorySettings={(categoryId) => {
          setServerSettingsInitialTab('Categories');
          setServerSettingsInitialEditId(categoryId);
          setShowServerSettingsModal(true);
        }}
        handleDeleteCategory={async (categoryId) => {
          // Set all channels in category to uncategorized first
          await supabase.from('channels').update({ category_id: null }).eq('category_id', categoryId);
          await supabase.from('categories').delete().eq('id', categoryId);
        }}
        myPrivateVaults={myPrivateVaults}
        handleInvite={handleInviteToVault}
        handleCreateInviteLink={handleCreateInviteLink}
        onDeleteVault={handleDeleteVault}
        setViewingProfileId={setViewingProfileId}
        handleAddFriend={handleFriendRequest}
        handleHideDM={handleHideDM}
        handleIgnoreUser={toggleMuteUser}
        handleBlockUser={toggleBlockUser}
        handleAddNickname={(userId) => {
          setSettingNicknameForUser(userId);
          setNicknameInput(nicknames[userId] || "");
        }}
        handleCreateChannelClick={() => { setEditingChannelId(null); setNewChannelName(""); setNewChannelType('text'); setShowCreateChannelModal(true); setContextMenu((prev: any) => ({ ...prev, visible: false })); }}
        handleEditChannelClick={(channelId: string) => { 
          const ch = channels.find(c => c.id === channelId);
          if (ch) {
            setEditingChannelId(channelId);
            setNewChannelName(ch.name);
            setNewChannelType(ch.type as 'text' | 'voice');
            setShowCreateChannelModal(true);
          }
          setContextMenu((prev: any) => ({ ...prev, visible: false }));
        }}
        handleDeleteChannel={handleDeleteChannel}
        openRoomSettings={openRoomSettings}
        handleOpenMembersModal={handleOpenMembersModal}
        handleCreateCategoryClick={() => { setServerSettingsInitialTab('Categories'); setServerSettingsInitialEditId(null); setShowServerSettingsModal(true); setContextMenu((prev: any) => ({ ...prev, visible: false })); }}
      />
      <Notification notifications={notifications} onDismiss={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} />

      {/* Vault Management Modals */}
      {showServerSettingsModal && (
        <ServerSettingsModal 
          vaultId={settingsVaultId || activeVault?.id || null} 
          initialTab={serverSettingsInitialTab}
          initialEditId={serverSettingsInitialEditId}
          onClose={() => {
            setShowServerSettingsModal(false);
            setServerSettingsInitialTab('Overview');
            setServerSettingsInitialEditId(null);
            setSettingsVaultId(null);
          }}
          onDelete={async (id) => {
             await supabase.from('vaults').delete().eq('id', id);
             if (activeVault?.id === id) setActiveVault(null);
          }}
        />
      )}
      {showChannelSettingsModal && (
        <ChannelSettingsModal
          channelId={editingSettingsChannelId}
          vaultId={activeVault?.id || ''}
          onClose={() => { setShowChannelSettingsModal(false); setEditingSettingsChannelId(null); }}
        />
      )}
      {showDiscordBridgeAdmin && (
        <DiscordBridgeAdminModal
          isOpen={showDiscordBridgeAdmin}
          onClose={() => setShowDiscordBridgeAdmin(false)}
          vaults={vaults}
          users={contacts}
        />
      )}
      <VaultManagementModals
        settingNicknameForUser={settingNicknameForUser}
        setSettingNicknameForUser={setSettingNicknameForUser}
        nicknameInput={nicknameInput}
        setNicknameInput={setNicknameInput}
        setNicknames={setNicknames}
        showCreateVaultModal={showCreateVaultModal}
        setShowCreateVaultModal={setShowCreateVaultModal}
        isEditingVault={isEditingVault}
        newVaultName={newVaultName}
        setNewRoomName={setNewRoomName}
        newVaultDesc={newVaultDesc}
        setNewRoomDesc={setNewRoomDesc}
        newVaultIcon={newVaultIcon}
        vaultIconInputRef={vaultIconInputRef}
        handleVaultIconUpload={handleVaultIconUpload}
        newVaultIsPublic={newVaultIsPublic}
        setNewRoomIsPublic={setNewRoomIsPublic}
        newVaultHasPassword={newVaultHasPassword}
        setNewRoomHasPassword={setNewRoomHasPassword}
        newVaultPassword={newVaultPassword}
        setNewRoomPassword={setNewRoomPassword}
        handleCreateVault={handleCreateVault}
        showCreateChannelModal={showCreateChannelModal}
        setShowCreateChannelModal={setShowCreateChannelModal}
        editingChannelId={editingChannelId}
        newChannelName={newChannelName}
        setNewChannelName={setNewChannelName}
        newChannelType={newChannelType}
        setNewChannelType={setNewChannelType}
        handleCreateChannel={handleCreateChannel}
        showMembersModal={showMembersModal}
        setShowMembersModal={setShowMembersModal}
        vaultMembers={vaultMembers}
        activeVault={activeVault}
        currentUser={currentUser}
        getUserFontStyle={getUserFontStyle}
        entityResolver={entityResolver}
        migrateLegacyBadge={migrateLegacyBadge}
        handleKickUserAction={handleKickUserAction}
        handleBanUserAction={handleBanUserAction}
      />

        {/* User Settings Modal */}
        <UserSettingsModal
          showUserSettings={showUserSettings}
          setShowUserSettings={setShowUserSettings}
          activeSettingsCategory={activeSettingsCategory}
          setActiveSettingsCategory={setActiveSettingsCategory}
          draftProfile={draftProfile}
          setDraftProfile={setDraftProfile}
          userProfile={userProfile}
          setUserProfile={setUserProfile}
          currentUser={currentUser}
          handleLogout={handleLogout}
          allowFriendFrom={allowFriendFrom}
          setAllowFriendFrom={setAllowFriendFrom}
          blockedUsers={blockedUsers}
          setBlockedUsers={setBlockedUsers}
          contacts={contacts}
          selectedAudioDevice={selectedAudioDevice}
          setSelectedAudioDevice={setSelectedAudioDevice}
          audioDevices={audioDevices}
          outputGain={outputGain}
          setOutputGain={setOutputGain}
          selectedVideoDevice={selectedVideoDevice}
          setSelectedVideoDevice={setSelectedVideoDevice}
          videoDevices={videoDevices}
          isPushToTalkEnabled={isPushToTalkEnabled}
          setIsPushToTalkEnabled={setIsPushToTalkEnabled}
          pushToTalkHotkey={pushToTalkHotkey}
          setPushToTalkHotkey={setPushToTalkHotkey}
          addNotification={addNotification}
        />

      <div className="fx"></div>
      <main
        className={`chat-container view-${view} show-${mobileView} ${activeVault && !isPipMode ? 'vault-active' : ''}`}
        id="chatContainer"
        onTouchStart={handleChatTouchStart}
        onTouchMove={handleChatTouchMove}
        onTouchEnd={handleChatTouchEnd}
        onTouchCancel={handleChatTouchEnd}
      >
        {mobileView === 'sidebar' && (
          <div 
            className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileView('chat')}
          />
        )}
        {/* Mobile swipe-back indicator — fades in as the user drags rightward
            from the left edge. Hidden on desktop via md:hidden. */}
        <div
          className="md:hidden fixed top-1/2 left-0 z-[60] pointer-events-none select-none"
          style={{
            transform: `translate(${8 + swipeProgress * 16}px, -50%)`,
            opacity: Math.min(1, swipeProgress * 1.4),
          }}
          aria-hidden="true"
        >
          <div className="bg-[var(--neon)]/90 rounded-r-2xl px-2 py-3 glow-active border-r border-white/30">
            <ChevronRight size={18} className="text-black" strokeWidth={3} />
          </div>
        </div>

        {/* Mobile-only full-screen call overlay. Renders a fresh CallLayout
            instance via .mobile-call-full which CSS promotes to position:fixed
            filling the viewport. This is independent of the in-place and PIP
            branches on desktop, so the existing JSX does not need to be
            wrapped. CSS hides other chat-container children when this branch
            is active. */}
        {mobileCallOverlayActive && (
          <div className="mobile-call-full">
            <CallLayout
              onWatchTogetherHostChange={setIsWatchTogetherHost}
              isPipMode={false}
              setIsPipMode={setIsPipMode}
              vaultId={activeVault?.id}
              channelId={callChannelId || undefined}
              currentUserId={currentUser?.id || 'me'}
              onLaunchGame={handleLaunchGame}
              initialGameId={activeGame}
              onAppGameExit={handleAppGameExit}
              pipPosition={pipPosition}
              setPipPosition={setPipPosition}
              participants={currentCallParticipants}
              onSendMessage={handleSendMessage}
              onUserContextMenu={handleUserContextMenu}
              activeSpeakerId={activeSpeakerId || 'me'}
              localStream={processedStreamRef.current || localStreamRef.current || null}
              screenStream={screenStreamRef.current}
              remoteStreams={remoteStreams}
              isScreenSharing={isScreenSharing}
              toggleScreenShare={toggleScreenShare}
              stopScreenShare={stopScreenShare}
              isVideoOn={isVideoOn}
              toggleVideo={toggleVideo}
              isMuted={isMuted}
              toggleMute={toggleMute}
              isDeafened={isDeafened}
              toggleDeafen={toggleDeafen}
              onEndCall={endCall}
              showSidebars={showSideChat}
              toggleSidebars={() => setShowSideChat(!showSideChat)}
              isMediaPlayerPoppedOut={false}
              setIsMediaPlayerPoppedOut={setIsMediaPlayerPoppedOut}
              sbVolume={sbVolume ?? 0.05}
              setSbVolume={setSbVolume}
              onPlaySoundboard={handlePlaySoundboard}
              onSendReaction={handleSendCallReaction}
              reactions={callReactions}
              remoteScreenStreams={remoteScreenStreams}
              layoutMode={layoutMode}
              setLayoutMode={setLayoutMode}
              audioSettings={{
                isLimiterEnabled: isLimiterEnabled ?? true,
                limiterThreshold: limiterThreshold ?? -1,
                isNormalizerEnabled: isNormalizerEnabled ?? true,
                normalizerThreshold: normalizerThreshold ?? -24,
                outputGain: outputGain ?? 1.0,
                isNoiseSuppressionEnabled: isNoiseSuppressionEnabled ?? true
              }}
              setAudioSettings={(s: any) => {
                setIsLimiterEnabled(s.isLimiterEnabled);
                setLimiterThreshold(s.limiterThreshold);
                setIsNormalizerEnabled(s.isNormalizerEnabled);
                setNormalizerThreshold(s.normalizerThreshold);
                setOutputGain(s.outputGain);
                setIsNoiseSuppressionEnabled(s.isNoiseSuppressionEnabled);
              }}
            />
          </div>
        )}


        <ServerRail
          vaults={vaults}
          discordServers={discordServers}
          activeVault={activeVault}
          vaultUnreadCounts={vaultUnreadCounts}
          onJoinRoom={handleJoinVault}
          onCreateRoom={() => setShowCreateVaultModal(true)}
          currentUserId={currentUser?.id}

          onDeleteVault={handleDeleteVault}
          onClearNodes={(adminModeEnabled && isPlatformAdminUser) ? handleClearAllNodes : undefined}
          onClearGuests={(adminModeEnabled && isPlatformAdminUser) ? handleClearAllGuests : undefined}
          onOpenDiscordAdmin={(adminModeEnabled && isPlatformAdminUser) ? () => setShowDiscordBridgeAdmin(true) : undefined}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          sidebarView={sidebarView}
          setSidebarView={setSidebarView}
          setActiveVault={setActiveVault}
          onServerContextMenu={(e, vaultId) => {
             e.preventDefault();
             setContextMenu({
               x: e.clientX,
               y: e.clientY,
               type: 'vault-header',
               targetId: vaultId,
               visible: true
             });
          }}
          mobileView={mobileView}
          setMobileView={setMobileView}
          isCinemaMode={isCinemaMode}
          contacts={contacts}
          activeContactId={activeContactId}
          setActiveContactId={(id) => {
            if (id) handleUnhideDM(id);
            setActiveContactId(id);
          }}
        />



        <ContextPanel
          mobileView={mobileView}
          isSidebarCollapsed={isSidebarCollapsed}
          isCinemaMode={isCinemaMode}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setMobileView={setMobileView}
          activeVault={activeVault}
          sidebarView={sidebarView}
          setSidebarView={setSidebarView}
          showVaultDropdown={showVaultDropdown}
          setShowVaultDropdown={setShowVaultDropdown}
          currentUser={currentUser}
          adminModeEnabled={adminModeEnabled}
          setContextMenu={setContextMenu}
          channels={activeDiscordGuildId ? discordChannels : channels}
          categories={activeDiscordGuildId ? discordCategories : categories}
          onReorderChannels={handleReorderChannels}
          voiceParticipants={voiceParticipants}
          isMuted={isMuted}
          isVideoOn={isVideoOn}
          isScreenSharing={isScreenSharing}
          peerVoiceStates={peerVoiceStates}
          activeSpeakerId={activeSpeakerId}
          getUserFontStyle={getUserFontStyle}
          playSound={playSound}
          setActiveChannelId={setActiveChannelId}
          setCallChannelId={setCallChannelId}
          setCallVaultId={setCallVaultId}
          setIsCallActive={setIsCallActive}
          handleChannelContextMenu={handleChannelContextMenu}
          handleSidebarAreaContextMenu={handleSidebarAreaContextMenu}
          activeChannelId={activeChannelId}
          isCallActive={isCallActive}
          setShowSideChat={setShowSideChat}
          contacts={contacts}
          contactsLoading={contactsLoading}
          dmRooms={dmRooms}
          activeContactId={activeContactId}
          setActiveContactId={(id) => {
            if (id) handleUnhideDM(id);
            setActiveContactId(id);
          }}
          setActiveVault={setActiveVault}
          hiddenDmUserIds={hiddenDmUserIds}
          onHideDM={handleHideDM}
          handleUserContextMenu={handleUserContextMenu}
          isTopBarPinned={isTopBarPinned}
          setIsTopBarPinned={setIsTopBarPinned}
          isTopBarExpanded={isTopBarExpanded}
          setIsTopBarExpanded={setIsTopBarExpanded}
          feedFilter={feedFilter}
          setFeedFilter={setFeedFilter}
          getUserRoleColor={getUserRoleColor}
        >
          <UserPanel
            userProfile={userProfile}
            setUserProfile={setUserProfile}
            currentUser={currentUser}
            isMuted={isMuted}
            toggleMute={toggleMute}
            isDeafened={isDeafened}
            toggleDeafen={toggleDeafen}
            isVideoOn={isVideoOn}
            toggleVideo={toggleVideo}
            toggleFullscreen={toggleFullscreen}
            showUserSettings={showUserSettings}
            setShowUserSettings={setShowUserSettings}
            showUiConfig={showUiConfig}
            setShowUiConfig={setShowUiConfig}
            bgTheme={bgTheme}
            setBgTheme={setBgTheme}
            baseHue={baseHue}
            setBaseHue={setBaseHue}
            getUserFontStyle={getUserFontStyle}
            onAvatarClick={() => avatarUploadInputRef.current?.click()}
            setViewingProfileId={setViewingProfileId}
          />
        </ContextPanel>

        {!mobileCallOverlayActive && (
        <section className={`message-view-panel chat-panel relative flex flex-col flex-grow`}>
          {activeTab === 'chat' && <NeuralBackground showText={!isCallActive && !activeVault && !activeContactId} />}
          {activeTab === 'feed' ? (
             <ErrorBoundary>
               <NeuralFeed
                 posts={filteredFeedPosts}
                 followedUsers={followedUsers}
                 onPost={handleCreateFeedPost}
                 onLike={handleLikeFeedPost}
                 onReply={handleReplyFeedPost}
                 onFollow={toggleFollow}
                 onViewProfile={handleViewProfile}
                 onClearFeed={handleClearFeed}
                 onDelete={handleDeleteFeedPost}
                 userProfile={userProfile}
                 currentUserId={currentUser?.id || ''}
                 currentUserEmail={currentUser?.email || null}
                 adminModeEnabled={adminModeEnabled}
                 getUserFontStyle={getUserFontStyle}
                 onUserContextMenu={handleUserContextMenu}
                 onJoinServer={handleJoinVault}
               />
             </ErrorBoundary>
          ) : (isCallActive && !isPipMode) ? (
            (() => {
              const currentParticipants = currentCallParticipants.map(p => {
                const isMe = p.id === currentUser?.id || p.id === 'me';
                const pState = isMe
                  ? { isMuted, isCameraOn: isVideoOn, isScreenSharing }
                  : (peerVoiceStates[p.id] || { isMuted: false, isCameraOn: false, isScreenSharing: false });
                return {
                  ...p,
                  isMuted: pState.isMuted,
                  isCameraOn: pState.isCameraOn,
                  isScreenSharing: pState.isScreenSharing
                };
              });
              const stream = localStreamRef.current;

              // Validate critical audio settings
              const safeAudioSettings = {
                isLimiterEnabled: isLimiterEnabled ?? true,
                limiterThreshold: limiterThreshold ?? -1,
                isNormalizerEnabled: isNormalizerEnabled ?? true,
                normalizerThreshold: normalizerThreshold ?? -24,
                outputGain: outputGain ?? 1.0,
                isNoiseSuppressionEnabled: isNoiseSuppressionEnabled ?? true
              };

              return (
                <CallLayout
                  onWatchTogetherHostChange={setIsWatchTogetherHost}
                  vaultId={activeVault?.id}
                  channelId={callChannelId || undefined}
                  currentUserId={currentUser?.id || 'me'}
              onLaunchGame={handleLaunchGame}
              initialGameId={activeGame}
              onAppGameExit={handleAppGameExit}
                  isPipMode={false}
                  setIsPipMode={setIsPipMode}
                  isCinemaMode={isCinemaMode}
                  setIsCinemaMode={setIsCinemaMode}
                  pipPosition={pipPosition}
                  setPipPosition={setPipPosition}
                  onEndCall={endCall}
                  participants={currentParticipants}
                  activeSpeakerId={activeSpeakerId || 'me'}
                  localStream={stream}
                  screenStream={screenStreamRef.current}
                  remoteScreenStreams={remoteScreenStreams}
                  isScreenSharing={isScreenSharing}
                  toggleScreenShare={toggleScreenShare}
                  stopScreenShare={stopScreenShare}
                  isVideoOn={isVideoOn || false}
                  toggleVideo={toggleVideo}
                  isMuted={isMuted || false}
                  toggleMute={toggleMute}
                  isDeafened={isDeafened || false}
                  toggleDeafen={toggleDeafen}
                  onUserContextMenu={handleUserContextMenu}
                  onSendMessage={handleSendMessage}
                  showSidebars={showSideChat}
                  toggleSidebars={() => setShowSideChat(!showSideChat)}
                  isMediaPlayerPoppedOut={isMediaPlayerPoppedOut || false}
                  setIsMediaPlayerPoppedOut={setIsMediaPlayerPoppedOut}
                  sbVolume={sbVolume ?? 0.05}
                  setSbVolume={setSbVolume}
                  onPlaySoundboard={handlePlaySoundboard}
                  onSendReaction={handleSendCallReaction}
                  reactions={callReactions || []}
                  remoteStreams={remoteStreams}
                  audioSettings={safeAudioSettings}
                  setAudioSettings={(settings: any) => {
                    if (settings.isLimiterEnabled !== undefined) setIsLimiterEnabled(settings.isLimiterEnabled);
                    if (settings.limiterThreshold !== undefined) setLimiterThreshold(settings.limiterThreshold);
                    if (settings.isNormalizerEnabled !== undefined) setIsNormalizerEnabled(settings.isNormalizerEnabled);
                    if (settings.normalizerThreshold !== undefined) setNormalizerThreshold(settings.normalizerThreshold);
                    if (settings.outputGain !== undefined) setOutputGain(settings.outputGain);
                    if (settings.isNoiseSuppressionEnabled !== undefined) setIsNoiseSuppressionEnabled(settings.isNoiseSuppressionEnabled);
                  }}
                  getUserFontStyle={getUserFontStyle}
                />
              );
            })()
          ) : (activeVault || activeContactId) ? (
            <div className={`relative z-10 flex-1 flex flex-col m-2 md:m-4 rounded-2xl backdrop-blur-xl ${bgTheme === 'synthwave' ? 'bg-black/75' : 'bg-black/40'} border border-white/10 shadow-2xl overflow-hidden text-white`}>
              {renderChatInterface()}
            </div>
          ) : null}
        </section>
        )}

        {showSideChat && isCallActive && !isPipMode && (
          <SideChatPanel 
             vaultId={activeVault?.id} 
          >
            <div className={`relative z-10 flex-1 flex flex-col m-2 md:m-4 rounded-2xl backdrop-blur-xl ${bgTheme === 'synthwave' ? 'bg-black/75' : 'bg-black/40'} border border-white/10 shadow-2xl overflow-hidden text-white`}>
              {renderChatInterface(isCallActive ? (activeChannelId === callChannelId ? undefined : activeChannelId) : undefined)}
            </div>
          </SideChatPanel>
        )}


      </main>

      {isPipMode && (
        (() => {
          let rawParticipants = [{ id: currentUser?.id || 'me', name: userProfile.name, avatar: userProfile.avatar }];
          if (callVaultId) {
            rawParticipants = voiceParticipants[callChannelId || 'vc'] || [{ id: currentUser?.id || 'me', name: userProfile.name, avatar: userProfile.avatar }];
          } else if (callChannelId) {
            const friend = contacts.find(c => c.id === callChannelId.replace('dm-', ''));
            if (friend) {
              rawParticipants.push({
                id: friend.id,
                name: friend.name,
                avatar: friend.avatar
              });
            }
          }
          const currentParticipants = rawParticipants.map(p => {
            const isMe = p.id === currentUser?.id || p.id === 'me';
            const pState = isMe
              ? { isMuted, isCameraOn: isVideoOn, isScreenSharing }
              : (peerVoiceStates[p.id] || { isMuted: false, isCameraOn: false, isScreenSharing: false });
            return {
              ...p,
              isMuted: pState.isMuted,
              isCameraOn: pState.isCameraOn,
              isScreenSharing: pState.isScreenSharing
            };
          });
          const stream = localStreamRef.current;
          const safeAudioSettings = {
            isLimiterEnabled: isLimiterEnabled ?? true,
            limiterThreshold: limiterThreshold ?? -1,
            isNormalizerEnabled: isNormalizerEnabled ?? true,
            normalizerThreshold: normalizerThreshold ?? -24,
            outputGain: outputGain ?? 1.0,
            isNoiseSuppressionEnabled: isNoiseSuppressionEnabled ?? true
          };

          return (
            <CallLayout
              onWatchTogetherHostChange={setIsWatchTogetherHost}
              vaultId={callVaultId}
              channelId={callChannelId || undefined}
              currentUserId={currentUser?.id || 'me'}
              onLaunchGame={handleLaunchGame}
              initialGameId={activeGame}
              onAppGameExit={handleAppGameExit}
              isPipMode={true}
              setIsPipMode={setIsPipMode}
              onReturnToCall={() => {
                if (callVaultId) {
                  const vault = vaults.find(v => v.id === callVaultId);
                  if (vault) {
                    setActiveVault(vault);
                    if (callChannelId) setActiveChannelId(callChannelId);
                    setActiveTab('chat');
                    setSidebarView('channels');
                  }
                } else if (callChannelId) {
                  setActiveVault(null);
                  setActiveContactId(callChannelId.replace('dm-', ''));
                  setSidebarView('dms');
                  setActiveTab('chat');
                }
              }}
              isCinemaMode={isCinemaMode}
              setIsCinemaMode={setIsCinemaMode}
              pipPosition={pipPosition}
              setPipPosition={setPipPosition}
              onEndCall={endCall}
              participants={currentParticipants}
              onSendMessage={handleSendMessage}
              onUserContextMenu={handleUserContextMenu}
              activeSpeakerId={activeSpeakerId || 'me'}
              localStream={stream}
              screenStream={screenStreamRef.current}
              remoteScreenStreams={remoteScreenStreams}
              isScreenSharing={isScreenSharing}
              toggleScreenShare={toggleScreenShare}
              stopScreenShare={stopScreenShare}
              isVideoOn={isVideoOn}
              toggleVideo={toggleVideo}
              isMuted={isMuted}
              toggleMute={toggleMute}
              remoteStreams={remoteStreams}
              isDeafened={isDeafened}
              toggleDeafen={toggleDeafen}
              showSidebars={false}
              toggleSidebars={() => {}}
              isMediaPlayerPoppedOut={false}
              setIsMediaPlayerPoppedOut={() => {}}
              sbVolume={sbVolume}
              setSbVolume={setSbVolume}
              onPlaySoundboard={() => {}}
              onSendReaction={() => {}}
              reactions={[]}
              audioSettings={safeAudioSettings}
              setAudioSettings={(settings: any) => {
                if (settings.isLimiterEnabled !== undefined) setIsLimiterEnabled(settings.isLimiterEnabled);
                if (settings.limiterThreshold !== undefined) setLimiterThreshold(settings.limiterThreshold);
                if (settings.isNormalizerEnabled !== undefined) setIsNormalizerEnabled(settings.isNormalizerEnabled);
                if (settings.normalizerThreshold !== undefined) setNormalizerThreshold(settings.normalizerThreshold);
                if (settings.outputGain !== undefined) setOutputGain(settings.outputGain);
                if (settings.isNoiseSuppressionEnabled !== undefined) setIsNoiseSuppressionEnabled(settings.isNoiseSuppressionEnabled);
              }}
              getUserFontStyle={getUserFontStyle}
            />
          );
        })()
      )}



      {/* USER PROFILE MODAL */}
      <ImageModal onPost={(text) => sendMessage(undefined, text)} />

      <AnimatePresence>
        {moderationDialog && (
          <ModerationDialog
            anchor={moderationDialog.anchor}
            targetId={moderationDialog.targetId}
            targetName={moderationDialog.targetName}
            vaultId={activeVault?.id ?? null}
            actorPerms={{
              canKick:    vaultPerms.hasPerm('KICK_MEMBERS'),
              canBan:     vaultPerms.hasPerm('BAN_MEMBERS'),
              canTimeout: vaultPerms.hasPerm('TIMEOUT_MEMBERS'),
              canMute:    vaultPerms.hasPerm('SERVER_MUTE'),
              canDeafen:  vaultPerms.hasPerm('SERVER_DEAFEN'),
            }}
            onClose={() => setModerationDialog(null)}
            onAfterAction={(action) => {
              addNotification({
                type: 'system',
                title: `Moderation: ${action}`,
                message: `Action applied to ${moderationDialog.targetName ?? moderationDialog.targetId}.`,
              });
            }}
          />
        )}
      </AnimatePresence>

      {viewingProfileId && (() => {
        // Some callers set the raw user id instead of the literal 'me'
        // sentinel (e.g. clicking your own row in the DM sidebar), so
        // treat your own id the same as 'me' to avoid rendering the
        // synthetic self-contact stub (scratchpad bio, Add Friend button).
        const isSelf = viewingProfileId === 'me' || viewingProfileId === currentUser?.id;
        const contact = (isSelf
          ? {
              id: 'me',
              name: userProfile.name,
              avatar: userProfile.avatar,
              status: userProfile.status,
              bio: userProfile.bio || 'This is you!',
              joinDate: 'Today',
              location: 'Localhost',
              type: 'human',
              customization: userProfile.customization,
              bannerType: userProfile.bannerType,
              bannerValue: userProfile.bannerValue,
              gradientHue1: userProfile.gradientHue1,
              gradientSat1: userProfile.gradientSat1,
              gradientHue2: userProfile.gradientHue2,
              gradientSat2: userProfile.gradientSat2,
              bannerImage: userProfile.bannerImage,
              profileBackgroundType: userProfile.profileBackgroundType,
              profileBackgroundValue: userProfile.profileBackgroundValue,
              profileBackgroundGradient: userProfile.profileBackgroundGradient,
              profileBackgroundImage: userProfile.profileBackgroundImage,
              profileTextStyle: userProfile.profileTextStyle,
              profileBorderStyle: userProfile.profileBorderStyle
            }
          : contacts.find(c => c.id === viewingProfileId)) as ChatContact | undefined;

        if (!contact) return null;

        return (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
             <div className="relative w-full max-w-2xl mx-auto flex flex-col gap-4 animate-in zoom-in-95 duration-200">
                <button
                  onClick={() => setViewingProfileId(null)}
                  className="absolute -top-12 right-0 z-[400] p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
                
                <ProfilePreview
                  profile={contact}
                  currentVaultId={activeVault?.id}
                  onAvatarClick={isSelf ? () => { setShowUserSettings(true); setActiveSettingsCategory('User Profile'); setViewingProfileId(null); } : undefined}
                  onAddFriend={!isSelf ? () => handleFriendRequest(contact.id) : undefined}
                  isFriend={friends.includes(contact.id)}
                  onSendDM={!isSelf ? () => { setViewingProfileId(null); setSidebarView('dms'); setActiveContactId(contact.id); } : undefined}
                  isSelf={isSelf}
                />

                
                {/* Mutual Friends & Shared Servers Info */}
                {!isSelf && (mutualFriends.length > 0 || mutualVaults.length > 0) && (
                  <div className="flex gap-3 items-center w-full justify-center">
                    {mutualFriends.length > 0 && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--neon)]/10 border border-[var(--neon)]/20 rounded-xl">
                        <Users size={14} className="text-[var(--neon)]" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-[var(--neon)]">
                          {mutualFriends.length} Mutual
                        </span>
                      </div>
                    )}
                    {mutualVaults.length > 0 && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
                        <Zap size={14} className="text-indigo-400" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">
                          {mutualVaults.length} Shared Server{mutualVaults.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                )}

             </div>
          </div>
        );
      })()}
      {/* DM CALL MODAL */}
      {dmCallState !== 'idle' && dmCallPeer && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in-50">
          <div className="bg-[#0b0e1a]/90 backdrop-blur-2xl border-2 border-white/10 rounded-[2.5rem] p-8 w-full max-w-sm shadow-2xl text-center flex flex-col items-center gap-6 relative overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full border border-[var(--neon)]/10 animate-ping pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full border border-[var(--neon)]/20 animate-pulse pointer-events-none" />
            
            <div className="relative">
              <div className="w-24 h-24 rounded-3xl border-2 border-[var(--neon)] overflow-hidden shadow-2xl relative z-10 animate-bounce">
                <img src={dmCallPeer.avatar} className="w-full h-full object-cover" />
              </div>
            </div>
            
            <div className="z-10">
              <h3 className="text-xl font-black text-white uppercase tracking-tighter mb-1">{dmCallPeer.name}</h3>
              <p className="text-xs text-[var(--neon)] uppercase tracking-[0.2em] font-mono animate-pulse">
                {dmCallState === 'calling' ? 'Calling Friend...' : 'Incoming Connection...'}
              </p>
            </div>
            
            <div className="flex gap-4 z-10 w-full mt-4 justify-center">
              {dmCallState === 'ringing' ? (
                <>
                  <button 
                    onClick={acceptDmCall}
                    className="flex-1 py-3 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase text-xs rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Phone size={16} /> Accept
                  </button>
                  <button 
                    onClick={declineDmCall}
                    className="flex-1 py-3 px-6 bg-red-500 hover:bg-red-600 text-white font-black uppercase text-xs rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <PhoneOff size={16} /> Decline
                  </button>
                </>
              ) : (
                <button 
                  onClick={cancelDmCall}
                  className="py-3 px-8 bg-red-500 hover:bg-red-600 text-white font-black uppercase text-xs rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <PhoneOff size={16} /> Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Vault Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 z-[350] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-slate-900 border border-red-500/30 rounded-2xl p-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-red-400 mb-2">Delete Server</h3>
            <p className="text-sm text-white/70 mb-6">
              Are you sure you want to delete <span className="font-bold text-white">{deleteConfirmation.vaultName}</span>? This action cannot be undone and all messages will be permanently lost.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="flex-1 py-2.5 px-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 py-2.5 px-4 bg-red-500/80 hover:bg-red-600 text-white font-bold rounded-lg transition-colors"
              >
                Delete Server
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

